import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissoes } from '../hooks/usePermissoes';
import { VisitasService } from '../services/visitasService';
import SelfieCapture from '../components/common/SelfieCapture';
import {
  motivosTransferenciaService,
  motivosCancelamentoService,
  motivosFinalizacaoService,
  tentativasVisitaService,
  motivosReprovacaoService,
  motivosReagendamentoService
} from '../services/visitasConfigService';
import {
  Visita,
  VisitaAgendaComprador,
  VisitaFechamento,
  VisitaSlot,
  VisitaIndicadores,
  StatusVisita
} from '../types';
import toast from 'react-hot-toast';

const diasSemanaCompleto = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const statusConfig: Record<StatusVisita, { label: string; cor: string }> = {
  'PENDENTE': { label: 'Pendente', cor: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  'APROVADO': { label: 'Aprovado', cor: 'bg-blue-100 text-blue-800 border-blue-300' },
  'REPROVADO': { label: 'Reprovado', cor: 'bg-red-100 text-red-800 border-red-300' },
  'REAGENDADO': { label: 'Proposta de Reagendamento', cor: 'bg-orange-100 text-orange-800 border-orange-300' },
  'CANCELADO': { label: 'Cancelado', cor: 'bg-gray-100 text-gray-800 border-gray-300' },
  'CHEGOU': { label: 'Chegou', cor: 'bg-green-100 text-green-800 border-green-300' },
  'EM_ATENDIMENTO': { label: 'Em Atendimento', cor: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  'FINALIZADO': { label: 'Finalizado', cor: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'NAO_COMPARECEU': { label: 'Não Compareceu', cor: 'bg-red-100 text-red-800 border-red-300' }
};

const fluxogramaStatus: StatusVisita[] = ['PENDENTE', 'APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO'];

const GestaoVisitas: React.FC = () => {
  const { user } = useAuth();
  const ehComprador = (user?.perfil || '').toLowerCase().includes('compr');
  const { temPermissao: podeVerAgenda } = usePermissoes('visitas', 'ver_agenda');
  const { temPermissao: podeVerCheckin } = usePermissoes('visitas', 'ver_checkin');
  const { temPermissao: podeVerSolicitacoes } = usePermissoes('visitas', 'ver_solicitacoes');
  const { temPermissao: podeVerIndicadores } = usePermissoes('visitas', 'ver_indicadores');
  const { temPermissao: podeAprovar } = usePermissoes('visitas', 'aprovar');
  const { temPermissao: podeReprovar } = usePermissoes('visitas', 'reprovar');
  const { temPermissao: podeReagendar } = usePermissoes('visitas', 'reagendar');
  const { temPermissao: podeConfigurarHorario } = usePermissoes('visitas', 'configurar_horario');
  const { temPermissao: podeFecharDia } = usePermissoes('visitas', 'fechar_dia');
  const { temPermissao: podeGerenciarAgenda } = usePermissoes('visitas', 'gerenciar_agenda');
  const [abaAtiva, setAbaAtiva] = useState<'agenda' | 'checkin' | 'solicitacoes' | 'indicadores'>('agenda');
  const [carregando, setCarregando] = useState(false);

  // Agenda
  const [configAgenda, setConfigAgenda] = useState<VisitaAgendaComprador[]>([]);
  const [dataSelecionada, setDataSelecionada] = useState<string>(new Date().toISOString().split('T')[0]);
  const [mesAtual, setMesAtual] = useState<Date>(new Date());
  const [slots, setSlots] = useState<VisitaSlot[]>([]);
  const [fechamentos, setFechamentos] = useState<VisitaFechamento[]>([]);
  const [modalFechamento, setModalFechamento] = useState(false);
  const [motivoFechamento, setMotivoFechamento] = useState('');

  // Configuração de horários
  const [configDias, setConfigDias] = useState<{ dia_semana: number; horario_inicio: string; horario_fim: string; horario_inicio_almoco?: string; horario_fim_almoco?: string; intervalo_minutos: number }[]>([]);
  const [modalConfig, setModalConfig] = useState(false);

  // Slots do dia
  const [slotsDiaEdicao, setSlotsDiaEdicao] = useState<{ inicio: string; fim: string; fechado: boolean; motivo?: string }[]>([]);

  // Grade semanal
  const [carregandoGrade, setCarregandoGrade] = useState(false);
  const [gradeSlots, setGradeSlots] = useState<Record<string, Record<string, { inicio: string; fim: string; fechado: boolean; motivo?: string; nomeVisitante?: string; visitaStatus?: string; fotoVisitante?: string; fotoSolicitante?: string; visitaId?: string }>>>({});
  const [visitasGrade, setVisitasGrade] = useState<Visita[]>([]);
  const [dataVisitasDia, setDataVisitasDia] = useState<string>(new Date().toISOString().split('T')[0]);
  const [diasSemanaGrade, setDiasSemanaGrade] = useState<string[]>([]);
  const [horariosGrade, setHorariosGrade] = useState<string[]>([]);
  const [slotEditando, setSlotEditando] = useState<{ data: string; inicio: string } | null>(null);

  // Solicitações
  const [visitasPendentes, setVisitasPendentes] = useState<Visita[]>([]);
  const [visitasAprovadas, setVisitasAprovadas] = useState<Visita[]>([]);
  const [visitasCanceladas, setVisitasCanceladas] = useState<Visita[]>([]);
  const [visitasReprovadas, setVisitasReprovadas] = useState<Visita[]>([]);
  const [buscaSolicitante, setBuscaSolicitante] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<StatusVisita[]>([]);
  const [dropdownStatusAberto, setDropdownStatusAberto] = useState(false);
  const [modalReagendar, setModalReagendar] = useState<Visita | null>(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [modalReprovar, setModalReprovar] = useState<Visita | null>(null);
  const [dataReagendamento, setDataReagendamento] = useState('');
  const [horaReagendamento, setHoraReagendamento] = useState('');
  const [slotsReagendamento, setSlotsReagendamento] = useState<VisitaSlot[]>([]);
  const [carregandoSlotsReagendamento, setCarregandoSlotsReagendamento] = useState(false);
  const [modalDetalhes, setModalDetalhes] = useState<Visita | null>(null);
  const [historicoVisita, setHistoricoVisita] = useState<any[]>([]);

  // Checkin
  const [visitasDia, setVisitasDia] = useState<Visita[]>([]);
  const [slotsCheckin, setSlotsCheckin] = useState<(VisitaSlot & { visita?: Visita })[]>([]);
  const [modalCheckin, setModalCheckin] = useState<Visita | null>(null);
  const [dadosCheckin, setDadosCheckin] = useState({ documento_confirmado: '', empresa_confirmada: '', nome_recepcionista: user?.nome || '', observacoes: '' });

  // Checkin - Grade semanal por comprador
  const [compradores, setCompradores] = useState<any[]>([]);
  const [compradorSelecionado, setCompradorSelecionado] = useState<string>('');
  const [gradeCheckin, setGradeCheckin] = useState<Record<string, Record<string, { inicio: string; fim: string; fechado: boolean; motivo?: string; nomeVisitante?: string; visitaStatus?: string; fotoVisitante?: string; fotoSolicitante?: string; visitaId?: string }>>>({});
  const [visitasGradeCheckin, setVisitasGradeCheckin] = useState<Visita[]>([]);
  const [diasSemanaCheckin, setDiasSemanaCheckin] = useState<string[]>([]);
  const [horariosCheckin, setHorariosCheckin] = useState<string[]>([]);
  const [carregandoGradeCheckin, setCarregandoGradeCheckin] = useState(false);
  const [fechamentosCheckin, setFechamentosCheckin] = useState<VisitaFechamento[]>([]);

  // Indicadores
  const [indicadores, setIndicadores] = useState<VisitaIndicadores | null>(null);
  const [fornecedorIndicadores, setFornecedorIndicadores] = useState<string>('');
  const [dataInicioIndicadores, setDataInicioIndicadores] = useState<string>(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dataFimIndicadores, setDataFimIndicadores] = useState<string>(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
  });

  // Transferência
  const [modalTransferir, setModalTransferir] = useState<Visita | null>(null);
  const [dadosTransferencia, setDadosTransferencia] = useState({ nova_data_visita: '', novo_horario_inicio: '', motivo_transferencia_id: '', motivo_transferencia_texto: '', observacao_transferencia: '' });
  const [motivosTransferencia, setMotivosTransferencia] = useState<any[]>([]);
  const [slotsTransferencia, setSlotsTransferencia] = useState<VisitaSlot[]>([]);

  // Cancelamento com motivo
  const [modalCancelar, setModalCancelar] = useState<Visita | null>(null);
  const [dadosCancelamento, setDadosCancelamento] = useState({ motivo_cancelamento_id: '', motivo_cancelamento_texto: '', observacao_cancelamento: '' });
  const [motivosCancelamento, setMotivosCancelamento] = useState<any[]>([]);

  // Não compareceu
  const [modalNaoCompareceu, setModalNaoCompareceu] = useState<Visita | null>(null);
  const [obsNaoCompareceu, setObsNaoCompareceu] = useState('');

  // Finalizar atendimento com motivo
  const [modalFinalizar, setModalFinalizar] = useState<Visita | null>(null);
  const [dadosFinalizacao, setDadosFinalizacao] = useState({ motivo_finalizacao_id: '', motivo_finalizacao_texto: '', observacao_finalizacao: '' });
  const [motivosFinalizacao, setMotivosFinalizacao] = useState<any[]>([]);

  // Tentativa de visita (sem agendamento)
  const [modalTentativaVisita, setModalTentativaVisita] = useState(false);
  const [dadosTentativa, setDadosTentativa] = useState({ fornecedor_nome: '', documento: '', empresa: '', assunto: '', email: '', telefone: '', data_visita: new Date().toISOString().split('T')[0], horario_inicio: '', horario_fim: '', observacoes: '' });
  const [fotoTentativa, setFotoTentativa] = useState<string | null>(null);
  const [tentativasVisita, setTentativasVisita] = useState<any[]>([]);
  const [modalDetalhesTentativa, setModalDetalhesTentativa] = useState<any | null>(null);
  const [motivosReprovacao, setMotivosReprovacao] = useState<any[]>([]);
  const [motivosReagendamento, setMotivosReagendamento] = useState<any[]>([]);
  const [motivoReagendamentoSelecionado, setMotivoReagendamentoSelecionado] = useState('');

  useEffect(() => {
    if (user?.id) {
      carregarAgenda();
      carregarFechamentos();
      if (podeVerSolicitacoes) carregarSolicitacoes();
      carregarMotivosVisitas();
      carregarTentativasVisita();
    }
  }, [user]);

  // Atualiza o balão de notificação de solicitações pendentes periodicamente
  useEffect(() => {
    if (!user?.id || !podeVerSolicitacoes) return;
    const intervalo = setInterval(() => carregarSolicitacoes(), 60000);
    return () => clearInterval(intervalo);
  }, [user, podeVerSolicitacoes]);

  useEffect(() => {
    carregarConfiguracaoInicial();
  }, [configAgenda]);

  useEffect(() => {
    if (user?.id && dataSelecionada) {
      carregarDisponibilidade();
      if (abaAtiva === 'checkin') carregarVisitasDia();
    }
  }, [user, dataSelecionada, abaAtiva, compradorSelecionado]);

  useEffect(() => {
    if (abaAtiva === 'solicitacoes') carregarSolicitacoes();
    if (abaAtiva === 'indicadores') carregarIndicadores();
    if (abaAtiva === 'checkin') {
      carregarVisitasDia();
      if (compradores.length === 0) carregarCompradores();
    }
    if (abaAtiva === 'agenda' && user?.id) {
      carregarGradeSemana();
      carregarTentativasVisita();
    }
  }, [abaAtiva, dataInicioIndicadores, dataFimIndicadores]);

  useEffect(() => {
    if (user?.id && abaAtiva === 'agenda') carregarGradeSemana();
  }, [dataSelecionada]);

  useEffect(() => {
    if (abaAtiva === 'checkin' && compradorSelecionado) {
      carregarGradeCheckin(compradorSelecionado);
    }
  }, [compradorSelecionado, dataSelecionada, abaAtiva]);

  const carregarAgenda = async () => {
    if (!user) return;
    const dados = await VisitasService.buscarAgenda(user.id);
    setConfigAgenda(dados);
  };

  const carregarConfiguracaoInicial = () => {
    if (configAgenda && configAgenda.length > 0) {
      const configs = configAgenda.map(c => ({
        dia_semana: c.dia_semana,
        horario_inicio: c.horario_inicio,
        horario_fim: c.horario_fim,
        horario_inicio_almoco: c.horario_inicio_almoco,
        horario_fim_almoco: c.horario_fim_almoco,
        intervalo_minutos: c.intervalo_minutos
      }));
      setConfigDias(configs);
      return;
    }
    const configs: { dia_semana: number; horario_inicio: string; horario_fim: string; horario_inicio_almoco?: string; horario_fim_almoco?: string; intervalo_minutos: number }[] = [];
    for (let i = 1; i <= 5; i++) {
      configs.push({ dia_semana: i, horario_inicio: '08:00', horario_fim: '17:00', horario_inicio_almoco: '12:00', horario_fim_almoco: '13:00', intervalo_minutos: 30 });
    }
    setConfigDias(configs);
  };

  const carregarDisponibilidade = async () => {
    if (!user) return;
    setCarregando(true);
    const resultado = await VisitasService.buscarDisponibilidade(user.id, dataSelecionada);
    setSlots(resultado.data);
    setCarregando(false);
  };

  const getInicioSemanaGestao = (dataStr: string): Date => {
    const d = new Date(dataStr + 'T12:00:00');
    const dia = d.getDay();
    const diff = dia === 0 ? -6 : 1 - dia;
    const seg = new Date(d);
    seg.setDate(d.getDate() + diff);
    return seg;
  };

  const carregarGradeSemana = async (dataRef?: string) => {
    if (!user) return;
    setCarregandoGrade(true);
    const ref = dataRef || dataSelecionada;
    const seg = getInicioSemanaGestao(ref);
    const dias: string[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(seg);
      d.setDate(seg.getDate() + i);
      dias.push(d.toISOString().split('T')[0]);
    }
    setDiasSemanaGrade(dias);

    // Busca slots salvos de cada dia
    const slotsPorDia = await Promise.all(
      dias.map(d => VisitasService.buscarSlotsDia(user.id, d))
    );

    // Se não há slots salvos, usa disponibilidade (que já aplica config semanal / padrão livre)
    const disponibilidadePorDia = await Promise.all(
      dias.map(d => VisitasService.buscarDisponibilidade(user.id, d))
    );

    // Busca visitas da semana para exibir nome do visitante e status
    const visitasSemana = await VisitasService.buscarVisitas({ comprador_id: user.id, status: ['PENDENTE', 'REAGENDADO', 'APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO'] });
    setVisitasGrade(visitasSemana || []);

    const grade: Record<string, Record<string, { inicio: string; fim: string; fechado: boolean; motivo?: string; nomeVisitante?: string; visitaStatus?: string; fotoVisitante?: string; fotoSolicitante?: string; visitaId?: string }>> = {};
    const horariosSet = new Set<string>();

    dias.forEach((data, i) => {
      grade[data] = {};
      const slotsSalvos = slotsPorDia[i];
      if (slotsSalvos && slotsSalvos.length > 0) {
        slotsSalvos.forEach((s: any) => {
          const ini = s.inicio.substring(0, 5);
          horariosSet.add(ini);
          grade[data][ini] = { inicio: ini, fim: s.fim.substring(0, 5), fechado: s.fechado || false, motivo: s.motivo || '' };
        });
      } else {
        disponibilidadePorDia[i].data.forEach((s: any) => {
          horariosSet.add(s.inicio);
          grade[data][s.inicio] = { inicio: s.inicio, fim: s.fim, fechado: s.ocupado || false, motivo: '' };
        });
      }

      // Cruza com visitas agendadas, sobrescreve o slot com nome do visitante
      (visitasSemana || []).filter((v: Visita) => v.data_visita === data).forEach((v: Visita) => {
        const ini = v.horario_inicio.substring(0, 5);
        horariosSet.add(ini);
        if (!grade[data][ini]) grade[data][ini] = { inicio: ini, fim: v.horario_fim.substring(0, 5), fechado: false };
        grade[data][ini] = { ...grade[data][ini], nomeVisitante: v.nome_visitante, visitaStatus: v.status, fotoVisitante: v.foto_visitante_url, fotoSolicitante: v.solicitante?.foto_perfil, visitaId: v.id };
      });
    });

    setHorariosGrade(Array.from(horariosSet).sort());
    setGradeSlots(grade);
    setCarregandoGrade(false);
  };

  const getDiasDoMes = (mes: Date): (number | null)[] => {
    const primeiroDia = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    const dias: (number | null)[] = [];
    for (let i = 0; i < primeiroDia.getDay(); i++) dias.push(null);
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) dias.push(dia);
    return dias;
  };

  const formatarDataCalendario = (ano: number, mes: number, dia: number): string => {
    return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  };

  const getCorDia = (dia: number) => {
    const hoje = new Date();
    const dataDia = new Date(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const dataDiaStr = formatarDataCalendario(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const diaSemana = dataDia.getDay();
    const isHoje = dataDia.toDateString() === hoje.toDateString();
    const isSelecionado = dataDiaStr === dataSelecionada;
    const fechamentoCompleto = fechamentos.some(f => f.data === dataDiaStr && f.ativo && !f.horario_inicio && !f.horario_fim);

    if (fechamentoCompleto) {
      return isSelecionado
        ? 'bg-red-700 text-white ring-2 ring-red-300'
        : 'bg-red-100 text-red-700 border-red-200';
    }

    if (diaSemana === 0 || diaSemana === 6) {
      return isSelecionado
        ? 'bg-red-600 text-white ring-2 ring-red-300'
        : isHoje
        ? 'bg-red-200 text-red-800 ring-2 ring-yellow-400 font-bold'
        : 'bg-red-100 text-red-700';
    }

    if (isSelecionado) return 'bg-blue-600 text-white ring-2 ring-blue-300 font-bold';
    if (isHoje) return 'bg-blue-100 text-blue-800 ring-2 ring-yellow-400 font-bold';
    return 'bg-white text-gray-700 hover:bg-blue-50';
  };

  const atualizarSlotsDiaEdicao = async (dataDiaStr: string) => {
    if (!user) return;
    const dataDia = new Date(dataDiaStr + 'T12:00:00');
    const diaSemana = dataDia.getDay();

    // Verificar se há slots personalizados salvos para esta data
    const slotsSalvos = await VisitasService.buscarSlotsDia(user.id, dataDiaStr);

    if (slotsSalvos.length > 0) {
      // Slots salvos ja trazem fechado/motivo, usa diretamente
      setSlotsDiaEdicao(slotsSalvos);
    } else {
      // Sem slots salvos: gera pela config base e cruza com fechamentos
      const slotsBase = gerarSlotsDia(diaSemana);
      const slotsComStatus = slotsBase.map(slot => {
        const fechamento = fechamentos.find(f =>
          f.data === dataDiaStr &&
          f.ativo &&
          f.horario_inicio === slot.inicio &&
          f.horario_fim === slot.fim
        );
        return {
          ...slot,
          fechado: !!fechamento,
          motivo: fechamento?.motivo || ''
        };
      });
      setSlotsDiaEdicao(slotsComStatus);
    }
  };

  const handleDiaClick = (dia: number) => {
    const dataDiaStr = formatarDataCalendario(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    setDataSelecionada(dataDiaStr);
  };

  useEffect(() => {
    if (!dataSelecionada || !user) return;
    atualizarSlotsDiaEdicao(dataSelecionada);
  }, [dataSelecionada, fechamentos, configAgenda, user]);

  const gerarSlotsDia = (diaSemana: number): { inicio: string; fim: string }[] => {
    const configs = configAgenda.filter(c => c.dia_semana === diaSemana && c.ativo);
    if (configs.length === 0) {
      // Configuração padrão
      return gerarSlotsIntervalo('08:00', '17:00', 60, '12:00', '13:00');
    }

    let todosSlots: { inicio: string; fim: string }[] = [];
    for (const config of configs) {
      const intervalo = config.intervalo_minutos || 60;
      todosSlots = todosSlots.concat(
        gerarSlotsIntervalo(config.horario_inicio, config.horario_fim, intervalo, config.horario_inicio_almoco, config.horario_fim_almoco)
      );
    }
    return todosSlots.sort((a, b) => a.inicio.localeCompare(b.inicio));
  };

  const gerarSlotsIntervalo = (horaInicio: string, horaFim: string, intervaloMinutos: number, horaInicioAlmoco?: string, horaFimAlmoco?: string): { inicio: string; fim: string }[] => {
    const slots = [];
    const [hi, mi] = horaInicio.split(':').map(Number);
    const [hf, mf] = horaFim.split(':').map(Number);
    let atual = hi * 60 + mi;
    const fim = hf * 60 + mf;

    const toStr = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
    const almocoInicio = horaInicioAlmoco ? converterParaMinutosLocal(horaInicioAlmoco) : null;
    const almocoFim = horaFimAlmoco ? converterParaMinutosLocal(horaFimAlmoco) : null;

    while (atual + intervaloMinutos <= fim) {
      const slotInicio = atual;
      const slotFim = atual + intervaloMinutos;
      const dentroAlmoco = almocoInicio && almocoFim && slotInicio >= almocoInicio && slotFim <= almocoFim;
      if (!dentroAlmoco) {
        slots.push({ inicio: toStr(slotInicio), fim: toStr(slotFim) });
      }
      atual = slotFim;
    }
    return slots;
  };

  const converterParaMinutosLocal = (hora: string) => {
    const [h, m] = hora.split(':').map(Number);
    return h * 60 + m;
  };

  const handleDiaClickConfigurar = (dia: number) => {
    const dataDia = new Date(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const dataDiaStr = formatarDataCalendario(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const diaSemana = dataDia.getDay();

    setDataSelecionada(dataDiaStr);

    const configsExistentes = configAgenda.filter(c => c.dia_semana === diaSemana);
    if (configsExistentes.length > 0) {
      setConfigDias(configsExistentes.map(c => ({
        dia_semana: c.dia_semana,
        horario_inicio: c.horario_inicio,
        horario_fim: c.horario_fim,
        horario_inicio_almoco: c.horario_inicio_almoco,
        horario_fim_almoco: c.horario_fim_almoco,
        intervalo_minutos: c.intervalo_minutos
      })));
    } else {
      const configs = [];
      for (let i = 1; i <= 5; i++) {
        configs.push({
          dia_semana: i,
          horario_inicio: '08:00',
          horario_fim: '17:00',
          horario_inicio_almoco: '12:00',
          horario_fim_almoco: '13:00',
          intervalo_minutos: 60
        });
      }
      setConfigDias(configs);
    }

    setModalConfig(true);
  };

  const carregarFechamentos = async () => {
    if (!user) return;
    const dados = await VisitasService.buscarFechamentos(user.id);
    setFechamentos(dados);
  };

  const abrirDetalhesVisita = async (visita: Visita) => {
    setModalDetalhes(visita);
    setHistoricoVisita([]);
    try {
      const hist = await VisitasService.buscarHistorico(visita.id);
      setHistoricoVisita(hist || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const carregarVisitasDia = async () => {
    if (!user) return;
    const compradorId = abaAtiva === 'checkin' && compradorSelecionado ? compradorSelecionado : user.id;
    const [dados, disponibilidade] = await Promise.all([
      VisitasService.buscarVisitas({
        comprador_id: compradorId,
        data_inicio: dataSelecionada,
        data_fim: dataSelecionada
      }),
      VisitasService.buscarDisponibilidade(compradorId, dataSelecionada)
    ]);
    setVisitasDia(dados);
    if (!disponibilidade.fechado && disponibilidade.data) {
      const slots = disponibilidade.data.map(s => {
        const visita = dados.find(v => v.horario_inicio?.substring(0, 5) === s.inicio && !['CANCELADO', 'NAO_COMPARECEU', 'REPROVADO'].includes(v.status));
        return { ...s, visita };
      });
      setSlotsCheckin(slots);
    } else {
      setSlotsCheckin([]);
    }
  };

  const carregarCompradores = async () => {
    try {
      // Comprador só pode ver a própria agenda
      if (ehComprador && user?.id) {
        setCompradores([{ id: user.id, nome: user.nome || 'Minha agenda' }]);
        setCompradorSelecionado(user.id);
        return;
      }
      const { data } = await VisitasService.buscarCompradores();
      setCompradores(data || []);
      if (data && data.length > 0 && !compradorSelecionado) {
        setCompradorSelecionado(data[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar compradores:', err);
    }
  };

  const carregarGradeCheckin = async (compradorId: string, dataRef?: string) => {
    if (!compradorId) return;
    setCarregandoGradeCheckin(true);
    const ref = dataRef || dataSelecionada;
    const seg = getInicioSemanaGestao(ref);
    const dias: string[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(seg);
      d.setDate(seg.getDate() + i);
      dias.push(d.toISOString().split('T')[0]);
    }
    setDiasSemanaCheckin(dias);

    const slotsPorDia = await Promise.all(
      dias.map(d => VisitasService.buscarSlotsDia(compradorId, d))
    );

    const disponibilidadePorDia = await Promise.all(
      dias.map(d => VisitasService.buscarDisponibilidade(compradorId, d))
    );

    const visitasSemana = await VisitasService.buscarVisitas({ comprador_id: compradorId, status: ['PENDENTE', 'REAGENDADO', 'APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO', 'NAO_COMPARECEU', 'CANCELADO'] });
    setVisitasGradeCheckin(visitasSemana || []);

    // Buscar fechamentos do comprador
    const fechamentosData = await VisitasService.buscarFechamentos(compradorId);
    setFechamentosCheckin(fechamentosData || []);

    const grade: Record<string, Record<string, { inicio: string; fim: string; fechado: boolean; motivo?: string; nomeVisitante?: string; visitaStatus?: string; fotoVisitante?: string; fotoSolicitante?: string; visitaId?: string }>> = {};
    const horariosSet = new Set<string>();

    dias.forEach((data, i) => {
      grade[data] = {};
      const slotsSalvos = slotsPorDia[i];
      if (slotsSalvos && slotsSalvos.length > 0) {
        slotsSalvos.forEach((s: any) => {
          const ini = s.inicio.substring(0, 5);
          horariosSet.add(ini);
          grade[data][ini] = { inicio: ini, fim: s.fim.substring(0, 5), fechado: s.fechado || false, motivo: s.motivo || '' };
        });
      } else {
        disponibilidadePorDia[i].data.forEach((s: any) => {
          horariosSet.add(s.inicio);
          grade[data][s.inicio] = { inicio: s.inicio, fim: s.fim, fechado: s.ocupado || false, motivo: '' };
        });
      }

      (visitasSemana || []).filter((v: Visita) => v.data_visita === data).forEach((v: Visita) => {
        const ini = v.horario_inicio.substring(0, 5);
        horariosSet.add(ini);
        if (!grade[data][ini]) grade[data][ini] = { inicio: ini, fim: v.horario_fim.substring(0, 5), fechado: false };
        grade[data][ini] = { ...grade[data][ini], nomeVisitante: v.nome_visitante, visitaStatus: v.status, fotoVisitante: v.foto_visitante_url, fotoSolicitante: v.solicitante?.foto_perfil, visitaId: v.id };
      });
    });

    setHorariosCheckin(Array.from(horariosSet).sort());
    setGradeCheckin(grade);
    setCarregandoGradeCheckin(false);
  };

  const carregarSolicitacoes = async () => {
    if (!user) return;
    setCarregando(true);
    const [pendentes, aprovadas, canceladas, reprovadas] = await Promise.all([
      VisitasService.buscarVisitas({ comprador_id: user.id, status: ['PENDENTE', 'REAGENDADO'] }),
      VisitasService.buscarVisitas({ comprador_id: user.id, status: ['APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO'] }),
      VisitasService.buscarVisitas({ comprador_id: user.id, status: ['CANCELADO', 'NAO_COMPARECEU'] }),
      VisitasService.buscarVisitas({ comprador_id: user.id, status: ['REPROVADO'] })
    ]);
    setVisitasPendentes(pendentes);
    setVisitasAprovadas(aprovadas);
    setVisitasCanceladas(canceladas);
    setVisitasReprovadas(reprovadas);
    setCarregando(false);
  };

  const carregarIndicadores = async () => {
    if (!user) return;
    setCarregando(true);
    const dados = await VisitasService.buscarIndicadores({
      comprador_id: user.id,
      data_inicio: dataInicioIndicadores,
      data_fim: dataFimIndicadores,
      fornecedor: fornecedorIndicadores.trim() || undefined
    });
    setIndicadores(dados);
    setCarregando(false);
  };

  const handleSalvarConfiguracao = async () => {
    if (!user) return;
    const toastId = toast.loading('Salvando configuração...');
    const { error } = await VisitasService.salvarAgenda(user.id, configDias);
    if (error) {
      toast.error(error.message || 'Erro ao salvar configuração', { id: toastId });
      return;
    }
    toast.success('Configuração salva!', { id: toastId });
    setModalConfig(false);
    carregarAgenda();
  };

  const handleAdicionarSlotDia = () => {
    setSlotsDiaEdicao([...slotsDiaEdicao, { inicio: '08:00', fim: '09:00', fechado: false, motivo: '' }]);
  };

  const handleRemoverSlotDia = (index: number) => {
    setSlotsDiaEdicao(slotsDiaEdicao.filter((_, i) => i !== index));
  };

  const handleAtualizarSlotDia = (index: number, campo: keyof typeof slotsDiaEdicao[0], valor: any) => {
    const novos = [...slotsDiaEdicao];
    novos[index] = { ...novos[index], [campo]: valor };
    setSlotsDiaEdicao(novos);
  };

  const handleSalvarSlotsDia = async () => {
    if (!user) return;
    const toastId = toast.loading('Salvando horários...');
    try {
      // 1. Salvar a lista de slots do dia no banco (todos, abertos e fechados)
      await VisitasService.salvarSlotsDia(user.id, dataSelecionada, slotsDiaEdicao);

      // 2. Reabrir fechamentos parciais do dia (remover os que estavam fechados)
      const fechamentosDoDia = fechamentos.filter(f => f.data === dataSelecionada && f.ativo && f.horario_inicio && f.horario_fim);
      for (const fechamento of fechamentosDoDia) {
        await VisitasService.reabrirDia(fechamento.id);
      }

      // 3. Criar novos fechamentos para slots marcados como fechados
      for (const slot of slotsDiaEdicao) {
        if (slot.fechado) {
          await VisitasService.fecharDia({
            comprador_id: user.id,
            data: dataSelecionada,
            horario_inicio: slot.inicio,
            horario_fim: slot.fim,
            motivo: slot.motivo || 'Horário fechado manualmente',
            tipo: 'manual'
          });
        }
      }

      toast.success('Horários salvos!', { id: toastId });
      carregarFechamentos();
      carregarDisponibilidade();
    } catch (error) {
      toast.error('Erro ao salvar horários', { id: toastId });
    }
  };

  const handleAdicionarDiaConfig = () => {
    setConfigDias([...configDias, { dia_semana: 1, horario_inicio: '08:00', horario_fim: '17:00', horario_inicio_almoco: '12:00', horario_fim_almoco: '13:00', intervalo_minutos: 30 }]);
  };

  const handleRemoverDiaConfig = (index: number) => {
    setConfigDias(configDias.filter((_, i) => i !== index));
  };

  const handleAtualizarDiaConfig = (index: number, campo: string, valor: any) => {
    const novos = [...configDias];
    novos[index] = { ...novos[index], [campo]: valor };
    setConfigDias(novos);
  };

  const handleFecharDia = async () => {
    if (!user) return;
    const toastId = toast.loading('Fechando dia...');
    const { error } = await VisitasService.fecharDia({
      comprador_id: user.id,
      data: dataSelecionada,
      motivo: motivoFechamento,
      tipo: 'manual'
    });
    if (error) {
      toast.error(error.message || 'Erro ao fechar dia', { id: toastId });
      return;
    }
    toast.success('Dia fechado!', { id: toastId });
    setModalFechamento(false);
    setMotivoFechamento('');
    carregarFechamentos();
    carregarDisponibilidade();
  };

  const handleReabrirDia = async (id: string) => {
    if (!window.confirm('Deseja reabrir este dia?')) return;
    const { error } = await VisitasService.reabrirDia(id);
    if (error) {
      toast.error(error.message || 'Erro ao reabrir dia');
      return;
    }
    toast.success('Dia reaberto!');
    carregarFechamentos();
    carregarDisponibilidade();
  };

  const handleAprovar = async (visita: Visita) => {
    const { error } = await VisitasService.aprovarVisita(visita.id);
    if (error) {
      toast.error(error.message || 'Erro ao aprovar');
      return;
    }
    toast.success('Visita aprovada!');
    carregarSolicitacoes();
  };

  const handleReprovar = async () => {
    if (!modalReprovar) return;
    const { error } = await VisitasService.reprovarVisita(modalReprovar.id, motivoReprovacao);
    if (error) {
      toast.error(error.message || 'Erro ao reprovar');
      return;
    }
    toast.success('Visita reprovada!');
    setModalReprovar(null);
    setMotivoReprovacao('');
    carregarSolicitacoes();
  };

  useEffect(() => {
    if (modalReagendar && dataReagendamento) {
      carregarSlotsReagendamento();
    }
  }, [modalReagendar, dataReagendamento]);

  const carregarSlotsReagendamento = async () => {
    if (!modalReagendar || !dataReagendamento) return;
    setCarregandoSlotsReagendamento(true);
    try {
      const resultado = await VisitasService.buscarDisponibilidade(modalReagendar.comprador_id, dataReagendamento);
      setSlotsReagendamento(resultado.data || []);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar horários disponíveis');
      setSlotsReagendamento([]);
    } finally {
      setCarregandoSlotsReagendamento(false);
    }
  };

  const handleReagendar = async () => {
    if (!modalReagendar || !horaReagendamento) return;
    if (!motivoReagendamentoSelecionado) {
      toast.error('Selecione um motivo de reagendamento');
      return;
    }
    const [horaInicio, horaFim] = horaReagendamento.split(' - ');
    if (!horaInicio || !horaFim) {
      toast.error('Selecione um horário disponível');
      return;
    }

    const { error } = await VisitasService.reagendarVisita(modalReagendar.id, {
      data_visita: dataReagendamento,
      horario_inicio: horaInicio,
      horario_fim: horaFim,
      motivo: motivoReagendamentoSelecionado
    });
    if (error) {
      toast.error(error.message || 'Erro ao reagendar');
      return;
    }
    toast.success('Visita reagendada!');
    setModalReagendar(null);
    setDataReagendamento('');
    setHoraReagendamento('');
    setSlotsReagendamento([]);
    setMotivoReagendamentoSelecionado('');
    carregarSolicitacoes();
  };

  const handleCheckin = async () => {
    if (!modalCheckin) return;
    const { error } = await VisitasService.realizarCheckin(modalCheckin.id, dadosCheckin);
    if (error) {
      toast.error(error.message || 'Erro no checkin');
      return;
    }
    toast.success('Checkin realizado! Email enviado ao comprador.');
    setModalCheckin(null);
    setDadosCheckin({ documento_confirmado: '', empresa_confirmada: '', nome_recepcionista: user?.nome || '', observacoes: '' });
    carregarVisitasDia();
  };

  const handleIniciarAtendimento = async (visita: Visita) => {
    const { error } = await VisitasService.iniciarAtendimento(visita.id);
    if (error) {
      toast.error(error.message || 'Erro ao iniciar atendimento');
      return;
    }
    toast.success('Atendimento iniciado!');
    carregarVisitasDia();
    carregarSolicitacoes();
  };

  const handleFinalizarAtendimento = async (visita: Visita) => {
    const { error } = await VisitasService.finalizarAtendimento(visita.id);
    if (error) {
      toast.error(error.message || 'Erro ao finalizar atendimento');
      return;
    }
    toast.success('Atendimento finalizado!');
    carregarVisitasDia();
    carregarSolicitacoes();
    carregarIndicadores();
  };

  // ===================== NOVOS HANDLERS =====================

  const carregarMotivosVisitas = async () => {
    try {
      const [trans, canc, final, reprov, reag] = await Promise.all([
        motivosTransferenciaService.listar(),
        motivosCancelamentoService.listar(),
        motivosFinalizacaoService.listar(),
        motivosReprovacaoService.listar(),
        motivosReagendamentoService.listar()
      ]);
      setMotivosTransferencia(trans?.data || []);
      setMotivosCancelamento(canc?.data || []);
      setMotivosFinalizacao(final?.data || []);
      setMotivosReprovacao(reprov?.data || []);
      setMotivosReagendamento(reag?.data || []);
    } catch (error) {
      console.error('Erro ao carregar motivos:', error);
    }
  };

  const abrirModalTransferir = async (visita: Visita) => {
    setModalTransferir(visita);
    setDadosTransferencia({ nova_data_visita: '', novo_horario_inicio: '', motivo_transferencia_id: '', motivo_transferencia_texto: '', observacao_transferencia: '' });
    setSlotsTransferencia([]);
    if (motivosTransferencia.length === 0) await carregarMotivosVisitas();
  };

  const handleTransferir = async () => {
    if (!modalTransferir) return;
    if (!dadosTransferencia.nova_data_visita || !dadosTransferencia.novo_horario_inicio || !dadosTransferencia.motivo_transferencia_texto) {
      toast.error('Data, horário e motivo são obrigatórios');
      return;
    }
    const { error } = await VisitasService.transferirVisita(modalTransferir.id, dadosTransferencia);
    if (error) {
      toast.error(error.message || 'Erro ao transferir visita');
      return;
    }
    toast.success('Visita transferida com sucesso!');
    setModalTransferir(null);
    carregarVisitasDia();
    carregarSolicitacoes();
    carregarGradeSemana();
  };

  const abrirModalCancelar = async (visita: Visita) => {
    setModalCancelar(visita);
    setDadosCancelamento({ motivo_cancelamento_id: '', motivo_cancelamento_texto: '', observacao_cancelamento: '' });
    if (motivosCancelamento.length === 0) await carregarMotivosVisitas();
  };

  const handleCancelarComMotivo = async () => {
    if (!modalCancelar) return;
    if (!dadosCancelamento.motivo_cancelamento_texto) {
      toast.error('Motivo de cancelamento é obrigatório');
      return;
    }
    const { error } = await VisitasService.cancelarComMotivo(modalCancelar.id, dadosCancelamento);
    if (error) {
      toast.error(error.message || 'Erro ao cancelar visita');
      return;
    }
    toast.success('Visita cancelada!');
    setModalCancelar(null);
    carregarVisitasDia();
    carregarSolicitacoes();
    carregarGradeSemana();
  };

  const handleNaoCompareceu = async () => {
    if (!modalNaoCompareceu) return;
    const { error } = await VisitasService.registrarNaoCompareceu(modalNaoCompareceu.id, obsNaoCompareceu);
    if (error) {
      toast.error(error.message || 'Erro ao registrar não comparecimento');
      return;
    }
    toast.success('Não comparecimento registrado!');
    setModalNaoCompareceu(null);
    setObsNaoCompareceu('');
    carregarVisitasDia();
    carregarSolicitacoes();
    carregarGradeSemana();
  };

  const abrirModalFinalizar = async (visita: Visita) => {
    setModalFinalizar(visita);
    setDadosFinalizacao({ motivo_finalizacao_id: '', motivo_finalizacao_texto: '', observacao_finalizacao: '' });
    if (motivosFinalizacao.length === 0) await carregarMotivosVisitas();
  };

  const handleFinalizarComMotivo = async () => {
    if (!modalFinalizar) return;
    if (!dadosFinalizacao.motivo_finalizacao_texto) {
      toast.error('Motivo de finalização é obrigatório');
      return;
    }
    const { error } = await VisitasService.finalizarComMotivo(modalFinalizar.id, {
      ...dadosFinalizacao
    });
    if (error) {
      toast.error(error.message || 'Erro ao finalizar atendimento');
      return;
    }
    toast.success('Atendimento finalizado com sucesso!');
    setModalFinalizar(null);
    carregarVisitasDia();
    carregarSolicitacoes();
    carregarIndicadores();
    carregarGradeSemana();
  };

  const handleCriarTentativaVisita = async () => {
    if (!user?.id) return;
    if (!dadosTentativa.fornecedor_nome || !dadosTentativa.data_visita) {
      toast.error('Nome do fornecedor e data são obrigatórios');
      return;
    }
    const compradorId = abaAtiva === 'checkin' && compradorSelecionado ? compradorSelecionado : user.id;
    try {
      await tentativasVisitaService.criar({
        comprador_id: compradorId,
        fornecedor_nome: dadosTentativa.fornecedor_nome,
        documento: dadosTentativa.documento,
        empresa: dadosTentativa.empresa,
        assunto: dadosTentativa.assunto,
        email: dadosTentativa.email,
        telefone: dadosTentativa.telefone,
        data_visita: dadosTentativa.data_visita,
        horario_inicio: dadosTentativa.horario_inicio,
        horario_fim: dadosTentativa.horario_fim,
        observacoes: dadosTentativa.observacoes,
        fotos: fotoTentativa ? [fotoTentativa] : []
      });
      toast.success('Tentativa de visita registrada! Aguardando aprovação do comprador.');
      setModalTentativaVisita(false);
      setDadosTentativa({ fornecedor_nome: '', documento: '', empresa: '', assunto: '', email: '', telefone: '', data_visita: new Date().toISOString().split('T')[0], horario_inicio: '', horario_fim: '', observacoes: '' });
      setFotoTentativa(null);
      carregarTentativasVisita();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao registrar tentativa de visita');
    }
  };

  const carregarTentativasVisita = async () => {
    if (!user?.id) return;
    try {
      const result = await tentativasVisitaService.listar(user.id);
      setTentativasVisita(result?.data || []);
    } catch (error) {
      console.error('Erro ao carregar tentativas:', error);
    }
  };

  const handleAprovarTentativa = async (id: string) => {
    try {
      await tentativasVisitaService.aprovar(id);
      toast.success('Tentativa aprovada! Visita criada com sucesso.');
      carregarTentativasVisita();
      carregarSolicitacoes();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao aprovar tentativa');
    }
  };

  const handleReprovarTentativa = async (id: string, motivo: string) => {
    try {
      await tentativasVisitaService.reprovar(id, motivo);
      toast.success('Tentativa reprovada.');
      carregarTentativasVisita();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao reprovar tentativa');
    }
  };

  const toggleStatusFiltro = (status: StatusVisita) => {
    setStatusFiltro(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const renderFluxograma = (statusAtual: StatusVisita) => {
    const indexAtual = fluxogramaStatus.indexOf(statusAtual);
    return (
      <div className="flex items-center gap-1 flex-wrap mt-2">
        {fluxogramaStatus.map((status, index) => {
          const ativo = index <= indexAtual;
          const atual = status === statusAtual;
          const config = statusConfig[status];
          return (
            <React.Fragment key={status}>
              <div className={`px-2 py-0.5 rounded text-xs font-medium border ${ativo ? config.cor : 'bg-gray-50 text-gray-400 border-gray-200'} ${atual ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}>
                {config.label}
              </div>
              {index < fluxogramaStatus.length - 1 && (
                <div className={`w-3 h-0.5 ${index < indexAtual ? 'bg-blue-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const fechamentoDia = fechamentos.find(f => f.data === dataSelecionada && f.ativo);

  const filtrarPorSolicitanteEStatus = (lista: Visita[]) => lista.filter(v =>
    (!buscaSolicitante || v.solicitante?.nome?.toLowerCase().includes(buscaSolicitante.toLowerCase())) &&
    (statusFiltro.length === 0 ? true : statusFiltro.includes(v.status))
  );

  // Quando não há filtro de status, mostrar apenas pendentes
  const filtrarPorSolicitanteDefault = (lista: Visita[]) => lista.filter(v =>
    (!buscaSolicitante || v.solicitante?.nome?.toLowerCase().includes(buscaSolicitante.toLowerCase())) &&
    (statusFiltro.length === 0 ? v.status === 'PENDENTE' || v.status === 'REAGENDADO' : statusFiltro.includes(v.status))
  );

  const visitasPendentesFiltradas = filtrarPorSolicitanteDefault(visitasPendentes);
  const visitasAprovadasFiltradas = filtrarPorSolicitanteEStatus(visitasAprovadas)
    .sort((a, b) => fluxogramaStatus.indexOf(a.status) - fluxogramaStatus.indexOf(b.status));
  const visitasCanceladasFiltradas = filtrarPorSolicitanteEStatus(visitasCanceladas);
  const visitasReprovadasFiltradas = filtrarPorSolicitanteEStatus(visitasReprovadas);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestão de Visitas</h1>
          <p className="text-gray-600 mt-1">Gerencie sua agenda, checkins e solicitações de visita.</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto border-b border-gray-200">
            {[
              { id: 'agenda', label: 'Minha Agenda', visivel: podeVerAgenda, badge: 0 },
              { id: 'checkin', label: 'Checkin', visivel: podeVerCheckin, badge: 0 },
              { id: 'solicitacoes', label: 'Solicitações', visivel: podeVerSolicitacoes, badge: visitasPendentes.length },
              { id: 'indicadores', label: 'Indicadores', visivel: podeVerIndicadores, badge: indicadores?.agendamentos_sem_visita_presencial ?? 0 }
            ].filter(aba => aba.visivel).map(aba => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id as any)}
                className={`relative px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap transition-colors ${abaAtiva === aba.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
              >
                {aba.label}
                {aba.badge > 0 && (
                  <span className="absolute -top-1 right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {aba.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {abaAtiva === 'agenda' && (
          <div className="space-y-6">
            {/* Tentativas de Visita Pendentes */}
            {tentativasVisita.filter(t => t.status === 'PENDENTE').length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl shadow-sm p-4">
                <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Tentativas de Visita Pendentes ({tentativasVisita.filter(t => t.status === 'PENDENTE').length})
                </h3>
                <div className="space-y-3">
                  {tentativasVisita.filter(t => t.status === 'PENDENTE').map((tentativa) => (
                    <div key={tentativa.id} className="bg-white rounded-lg border border-amber-200 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{tentativa.fornecedor_nome}</p>
                        <p className="text-xs text-gray-500">
                          {tentativa.empresa && `${tentativa.empresa} • `}
                          {new Date(tentativa.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')}
                          {tentativa.horario_inicio && ` • ${tentativa.horario_inicio.substring(0, 5)}${tentativa.horario_fim ? ' - ' + tentativa.horario_fim.substring(0, 5) : ''}`}
                        </p>
                        {tentativa.observacoes && (
                          <p className="text-xs text-gray-400 mt-1 truncate">{tentativa.observacoes}</p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => setModalDetalhesTentativa(tentativa)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Ver Detalhes
                        </button>
                        <button
                          onClick={() => handleAprovarTentativa(tentativa.id)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                        >
                          Aprovar
                        </button>
                        <button
                          onClick={() => {
                            const motivo = prompt('Motivo da reprovação:');
                            if (motivo) handleReprovarTentativa(tentativa.id, motivo);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          Reprovar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Grade semanal */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Minha Agenda</h2>
                  <p className="text-sm text-gray-500">
                    Semana de {diasSemanaGrade[0] ? new Date(diasSemanaGrade[0] + 'T12:00:00').toLocaleDateString('pt-BR') : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const seg = getInicioSemanaGestao(diasSemanaGrade[0] || dataSelecionada);
                      const semAnterior = new Date(seg);
                      semAnterior.setDate(seg.getDate() - 7);
                      const nova = semAnterior.toISOString().split('T')[0];
                      setDataSelecionada(nova);
                    }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDataSelecionada(new Date().toISOString().split('T')[0])}
                    className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    Hoje
                  </button>
                  <button
                    onClick={() => {
                      const seg = getInicioSemanaGestao(diasSemanaGrade[0] || dataSelecionada);
                      const proxSemana = new Date(seg);
                      proxSemana.setDate(seg.getDate() + 7);
                      const nova = proxSemana.toISOString().split('T')[0];
                      setDataSelecionada(nova);
                    }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {podeConfigurarHorario && (
                    <button
                      onClick={() => setModalConfig(true)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Configurar Horários
                    </button>
                  )}
                </div>
              </div>

              {carregandoGrade ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div>
                  {/* Desktop: tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="py-2 px-3 text-left text-gray-500 font-medium w-20">Horário</th>
                          {diasSemanaGrade.map(data => {
                            const d = new Date(data + 'T12:00:00');
                            const isHoje = d.toDateString() === new Date().toDateString();
                            const fechamentoCompleto = fechamentos.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                            return (
                              <th key={data} className={`py-2 px-3 text-center font-medium ${isHoje ? 'text-yellow-600' : 'text-gray-700'}`}>
                                <div>{['Seg','Ter','Qua','Qui','Sex'][diasSemanaGrade.indexOf(data)]}</div>
                                <div className="text-xs font-normal text-gray-400">
                                  {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </div>
                                {fechamentoCompleto ? (
                                  <div className="mt-1 flex flex-col items-center gap-1">
                                    <span className="text-xs text-red-600 font-normal">Fechado</span>
                                    {podeFecharDia && (
                                      <button onClick={() => handleReabrirDia(fechamentoCompleto.id)} className="text-xs text-blue-500 hover:underline font-normal">Reabrir</button>
                                    )}
                                  </div>
                                ) : podeFecharDia ? (
                                  <button
                                    onClick={() => { setDataSelecionada(data); setModalFechamento(true); }}
                                    className="mt-1 text-xs text-gray-400 hover:text-red-500 font-normal"
                                  >
                                    Fechar dia
                                  </button>
                                ) : null}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {horariosGrade.map(horario => (
                          <tr key={horario} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-500 font-medium text-xs">{horario}</td>
                            {diasSemanaGrade.map(data => {
                              const slot = gradeSlots[data]?.[horario];
                              const isEditando = slotEditando?.data === data && slotEditando?.inicio === horario;
                              const fechamentoCompleto = fechamentos.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                              if (fechamentoCompleto) {
                                return <td key={data} className="py-2 px-3 text-center bg-red-50"><span className="text-xs text-red-300">-</span></td>;
                              }
                              if (!slot) {
                                return (
                                  <td key={data} className="py-2 px-3 text-center">
                                    {podeConfigurarHorario ? (
                                      <button
                                        onClick={() => {
                                          const novaGrade = { ...gradeSlots };
                                          if (!novaGrade[data]) novaGrade[data] = {};
                                          novaGrade[data][horario] = { inicio: horario, fim: horario, fechado: false };
                                          setGradeSlots(novaGrade);
                                          setSlotEditando({ data, inicio: horario });
                                        }}
                                        className="text-xs text-gray-300 hover:text-blue-500"
                                      >+</button>
                                    ) : <span className="text-gray-300 text-xs">-</span>}
                                  </td>
                                );
                              }
                              if (isEditando && podeConfigurarHorario) {
                                return (
                                  <td key={data} className="py-1 px-1 text-center">
                                    <div className="flex flex-col gap-1 min-w-[90px]">
                                      <div className="flex gap-1">
                                        <input type="time" value={slot.inicio} onChange={e => {
                                          const ng = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, inicio: e.target.value } } };
                                          setGradeSlots(ng);
                                        }} className="w-full text-xs rounded border-gray-300 p-0.5" />
                                        <input type="time" value={slot.fim} onChange={e => {
                                          const ng = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, fim: e.target.value } } };
                                          setGradeSlots(ng);
                                        }} className="w-full text-xs rounded border-gray-300 p-0.5" />
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={async () => {
                                            const diaSlots = Object.values(gradeSlots[data] || {});
                                            await VisitasService.salvarSlotsDia(user!.id, data, diaSlots);
                                            toast.success('Salvo!');
                                            setSlotEditando(null);
                                            carregarGradeSemana();
                                          }}
                                          className="flex-1 text-xs py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >✓</button>
                                        <button
                                          onClick={() => setSlotEditando(null)}
                                          className="flex-1 text-xs py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                        >✕</button>
                                      </div>
                                    </div>
                                  </td>
                                );
                              }
                              const temVisita = !!slot.nomeVisitante;
                              const isPendente = slot.visitaStatus === 'PENDENTE';
                              const isReagendado = slot.visitaStatus === 'REAGENDADO';
                              const corVisita = isPendente
                                ? 'bg-yellow-100 text-yellow-800'
                                : isReagendado
                                ? 'bg-orange-100 text-orange-800'
                                : slot.visitaStatus === 'APROVADO' || slot.visitaStatus === 'CHEGOU' || slot.visitaStatus === 'EM_ATENDIMENTO'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700';
                              const labelVisita = isPendente
                                ? 'Em análise'
                                : isReagendado
                                ? 'Proposta de Reagendamento'
                                : slot.nomeVisitante;
                              return (
                                <td key={data} className="py-2 px-3 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    {temVisita ? (
                                      <button
                                        onClick={() => {
                                          const visita = visitasGrade.find(v => v.id === slot.visitaId);
                                          if (visita) abrirDetalhesVisita(visita);
                                        }}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium max-w-[110px] truncate ${corVisita} hover:opacity-80 cursor-pointer transition-opacity`}
                                        title={`${slot.nomeVisitante} (${slot.visitaStatus}) - Clique para ver detalhes`}
                                      >
                                        {(slot.fotoVisitante || slot.fotoSolicitante) ? (
                                          <img src={slot.fotoVisitante || slot.fotoSolicitante} alt={slot.nomeVisitante} className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-white" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
                                        )}
                                        {labelVisita}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => podeConfigurarHorario ? setSlotEditando({ data, inicio: horario }) : undefined}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                          slot.fechado
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-green-100 text-green-700'
                                        } ${podeConfigurarHorario ? 'hover:opacity-80 cursor-pointer' : ''}`}
                                      >
                                        <span className={`w-2 h-2 rounded-full ${slot.fechado ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                        {slot.fechado ? 'Fechado' : 'Livre'}
                                      </button>
                                    )}
                                    {podeConfigurarHorario && !temVisita && (
                                      <button
                                        onClick={async () => {
                                          const novaGrade = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, fechado: !slot.fechado } } };
                                          setGradeSlots(novaGrade);
                                          const diaSlots = Object.values(novaGrade[data] || {});
                                          await VisitasService.salvarSlotsDia(user!.id, data, diaSlots);
                                          toast.success(slot.fechado ? 'Horário aberto!' : 'Horário fechado!');
                                          carregarGradeSemana();
                                        }}
                                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                                      >
                                        {slot.fechado ? 'Reabrir' : 'Fechar'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: cards por dia */}
                  <div className="md:hidden space-y-3">
                    {diasSemanaGrade.map(data => {
                      const d = new Date(data + 'T12:00:00');
                      const isHoje = d.toDateString() === new Date().toDateString();
                      const fechamentoCompleto = fechamentos.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                      const diaLabel = ['Seg','Ter','Qua','Qui','Sex'][diasSemanaGrade.indexOf(data)];
                      return (
                        <div key={data} className="rounded-lg border border-gray-200 bg-white p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-semibold ${isHoje ? 'text-yellow-600' : 'text-gray-800'}`}>
                              {diaLabel}, {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                            </span>
                            {fechamentoCompleto ? (
                              <span className="text-xs text-red-600 font-medium">Fechado</span>
                            ) : podeFecharDia ? (
                              <button
                                onClick={() => { setDataSelecionada(data); setModalFechamento(true); }}
                                className="text-xs text-gray-400 hover:text-red-500"
                              >
                                Fechar dia
                              </button>
                            ) : null}
                          </div>
                          {fechamentoCompleto ? (
                            <div className="text-center py-4 bg-red-50 rounded-lg">
                              <p className="text-xs text-red-600">Dia fechado</p>
                              {podeFecharDia && (
                                <button onClick={() => handleReabrirDia(fechamentoCompleto.id)} className="text-xs text-blue-500 hover:underline mt-1">Reabrir</button>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {horariosGrade.map(horario => {
                                const slot = gradeSlots[data]?.[horario];
                                if (!slot) {
                                  return podeConfigurarHorario ? (
                                    <button
                                      key={horario}
                                      onClick={() => {
                                        const novaGrade = { ...gradeSlots };
                                        if (!novaGrade[data]) novaGrade[data] = {};
                                        novaGrade[data][horario] = { inicio: horario, fim: horario, fechado: false };
                                        setGradeSlots(novaGrade);
                                        setSlotEditando({ data, inicio: horario });
                                      }}
                                      className="text-xs text-gray-300 hover:text-blue-500 border border-dashed border-gray-300 rounded-lg py-2"
                                    >+ {horario}</button>
                                  ) : null;
                                }
                                const isEditando = slotEditando?.data === data && slotEditando?.inicio === horario;
                                if (isEditando && podeConfigurarHorario) {
                                  return (
                                    <div key={horario} className="col-span-2 flex flex-col gap-1 p-2 bg-gray-50 rounded-lg">
                                      <div className="flex gap-2">
                                        <input type="time" value={slot.inicio} onChange={e => {
                                          const ng = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, inicio: e.target.value } } };
                                          setGradeSlots(ng);
                                        }} className="w-full text-xs rounded border-gray-300 p-1" />
                                        <input type="time" value={slot.fim} onChange={e => {
                                          const ng = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, fim: e.target.value } } };
                                          setGradeSlots(ng);
                                        }} className="w-full text-xs rounded border-gray-300 p-1" />
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={async () => {
                                            const diaSlots = Object.values(gradeSlots[data] || {});
                                            await VisitasService.salvarSlotsDia(user!.id, data, diaSlots);
                                            toast.success('Salvo!');
                                            setSlotEditando(null);
                                            carregarGradeSemana();
                                          }}
                                          className="flex-1 text-xs py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >✓ Salvar</button>
                                        <button
                                          onClick={() => setSlotEditando(null)}
                                          className="flex-1 text-xs py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                        >✕ Cancelar</button>
                                      </div>
                                    </div>
                                  );
                                }
                                const temVisita = !!slot.nomeVisitante;
                                const isPendente = slot.visitaStatus === 'PENDENTE';
                                const isReagendado = slot.visitaStatus === 'REAGENDADO';
                                const corVisita = isPendente
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : isReagendado
                                  ? 'bg-orange-100 text-orange-800'
                                  : slot.visitaStatus === 'APROVADO' || slot.visitaStatus === 'CHEGOU' || slot.visitaStatus === 'EM_ATENDIMENTO'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-700';
                                const labelVisita = isPendente
                                  ? 'Em análise'
                                  : isReagendado
                                  ? 'Proposta de Reagendamento'
                                  : slot.nomeVisitante;
                                return (
                                  <div key={horario} className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-100">
                                    {temVisita ? (
                                      <button
                                        onClick={() => {
                                          const visita = visitasGrade.find(v => v.id === slot.visitaId);
                                          if (visita) abrirDetalhesVisita(visita);
                                        }}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium truncate w-full justify-center ${corVisita} hover:opacity-80 cursor-pointer transition-opacity`}
                                        title={`${slot.nomeVisitante} (${slot.visitaStatus}) - Clique para ver detalhes`}
                                      >
                                        {(slot.fotoVisitante || slot.fotoSolicitante) ? (
                                          <img src={slot.fotoVisitante || slot.fotoSolicitante} alt={slot.nomeVisitante} className="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-white" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
                                        )}
                                        {labelVisita}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => podeConfigurarHorario ? setSlotEditando({ data, inicio: horario }) : undefined}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                          slot.fechado
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-green-100 text-green-700'
                                        } ${podeConfigurarHorario ? 'hover:opacity-80 cursor-pointer' : ''}`}
                                      >
                                        <span className={`w-2 h-2 rounded-full ${slot.fechado ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                        {slot.fechado ? 'Fechado' : 'Livre'}
                                      </button>
                                    )}
                                    <span className="text-xs text-gray-400">{horario}</span>
                                    {podeConfigurarHorario && !temVisita && (
                                      <button
                                        onClick={async () => {
                                          const novaGrade = { ...gradeSlots, [data]: { ...gradeSlots[data], [horario]: { ...slot, fechado: !slot.fechado } } };
                                          setGradeSlots(novaGrade);
                                          const diaSlots = Object.values(novaGrade[data] || {});
                                          await VisitasService.salvarSlotsDia(user!.id, data, diaSlots);
                                          toast.success(slot.fechado ? 'Horário aberto!' : 'Horário fechado!');
                                          carregarGradeSemana();
                                        }}
                                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                                      >
                                        {slot.fechado ? 'Reabrir' : 'Fechar'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500"></span><span>Horário Livre</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500"></span><span>Horário Fechado</span></div>
                    {podeConfigurarHorario && <span className="text-gray-400">Clique no status para editar. Clique em Fechar/Reabrir para alternar.</span>}
                  </div>
                </div>
              )}

              {/* Visitas do Dia */}
              {(() => {
                const visitasDia = visitasGrade.filter(v => v.data_visita === dataVisitasDia);

                return (
                  <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Visitas do Dia</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const d = new Date(dataVisitasDia + 'T12:00:00');
                            d.setDate(d.getDate() - 1);
                            setDataVisitasDia(d.toISOString().split('T')[0]);
                          }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <input
                          type="date"
                          value={dataVisitasDia}
                          onChange={(e) => setDataVisitasDia(e.target.value)}
                          className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                        />
                        <button
                          onClick={() => {
                            const d = new Date(dataVisitasDia + 'T12:00:00');
                            d.setDate(d.getDate() + 1);
                            setDataVisitasDia(d.toISOString().split('T')[0]);
                          }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDataVisitasDia(new Date().toISOString().split('T')[0])}
                          className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                        >
                          Hoje
                        </button>
                      </div>
                    </div>

                    {visitasDia.length === 0 ? (
                      <div className="py-8 text-center text-gray-400">
                        <p className="text-sm">Nenhuma visita agendada para este dia.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {visitasDia
                          .sort((a, b) => (a.horario_inicio || '').localeCompare(b.horario_inicio || ''))
                          .map((visita) => {
                            const status = visita.status as StatusVisita;
                            const indexAtual = fluxogramaStatus.indexOf(status);
                            const config = statusConfig[status] || statusConfig['PENDENTE'];
                            const foto = visita.foto_visitante_url || visita.solicitante?.foto_perfil;
                            return (
                              <button
                                key={visita.id}
                                onClick={() => abrirDetalhesVisita(visita)}
                                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 w-full text-left transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {foto ? (
                                    <img src={foto} alt={visita.nome_visitante} className="w-10 h-10 rounded-full object-cover border border-gray-300" />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-300 flex items-center justify-center">
                                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{visita.nome_visitante}</p>
                                    <p className="text-xs text-gray-500">{visita.horario_inicio?.substring(0, 5)} - {visita.horario_fim?.substring(0, 5)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap flex-1">
                                  {fluxogramaStatus.map((st, index) => {
                                    const ativo = index <= indexAtual;
                                    const atual = st === status;
                                    const cfg = statusConfig[st];
                                    return (
                                      <React.Fragment key={st}>
                                        <div className={`px-2 py-0.5 rounded text-xs font-medium border ${ativo ? cfg.cor : 'bg-gray-50 text-gray-400 border-gray-200'} ${atual ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}>
                                          {cfg.label}
                                        </div>
                                        {index < fluxogramaStatus.length - 1 && (
                                          <div className={`w-3 h-0.5 ${index < indexAtual ? 'bg-blue-500' : 'bg-gray-200'}`} />
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {abaAtiva === 'checkin' && (
          <div className="space-y-6">
            {/* Grade semanal por comprador */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Agenda de Compradores</h2>
                <div className="flex items-center gap-2">
                  {!ehComprador && (
                    <select
                      className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                      value={compradorSelecionado}
                      onChange={(e) => setCompradorSelecionado(e.target.value)}
                    >
                      {compradores.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="date"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                    value={dataSelecionada}
                    onChange={(e) => setDataSelecionada(e.target.value)}
                  />
                </div>
              </div>

              {carregandoGradeCheckin ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : horariosCheckin.length === 0 ? (
                <div className="p-6 text-center text-gray-500">Nenhum horário configurado para esta semana.</div>
              ) : (
                <div>
                  {/* Desktop: tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="py-2 px-3 text-left text-gray-500 font-medium w-20">Horário</th>
                          {diasSemanaCheckin.map(data => {
                            const d = new Date(data + 'T12:00:00');
                            const isHoje = d.toDateString() === new Date().toDateString();
                            const fechamentoCompleto = fechamentosCheckin.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                            return (
                              <th key={data} className={`py-2 px-3 text-center font-medium ${isHoje ? 'text-yellow-600' : 'text-gray-700'}`}>
                                <div>{['Seg','Ter','Qua','Qui','Sex'][diasSemanaCheckin.indexOf(data)]}</div>
                                <div className="text-xs font-normal text-gray-400">
                                  {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </div>
                                {fechamentoCompleto && (
                                  <div className="mt-1"><span className="text-xs text-red-600 font-normal">Fechado</span></div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {horariosCheckin.map(horario => (
                          <tr key={horario} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-500 font-medium text-xs">{horario}</td>
                            {diasSemanaCheckin.map(data => {
                              const slot = gradeCheckin[data]?.[horario];
                              const fechamentoCompleto = fechamentosCheckin.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                              if (fechamentoCompleto) {
                                return <td key={data} className="py-2 px-3 text-center bg-red-50"><span className="text-xs text-red-300">-</span></td>;
                              }
                              if (!slot) {
                                return <td key={data} className="py-2 px-3 text-center"><span className="text-gray-300 text-xs">-</span></td>;
                              }
                              const temVisita = !!slot.nomeVisitante;
                              const isPendente = slot.visitaStatus === 'PENDENTE';
                              const isReagendado = slot.visitaStatus === 'REAGENDADO';
                              const corVisita = isPendente
                                ? 'bg-yellow-100 text-yellow-800'
                                : isReagendado
                                ? 'bg-orange-100 text-orange-800'
                                : slot.visitaStatus === 'APROVADO' || slot.visitaStatus === 'CHEGOU' || slot.visitaStatus === 'EM_ATENDIMENTO'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700';
                              const labelVisita = isPendente
                                ? 'Em análise'
                                : isReagendado
                                ? 'Proposta de Reagendamento'
                                : slot.nomeVisitante;
                              return (
                                <td key={data} className="py-2 px-3 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    {temVisita ? (
                                      <button
                                        onClick={() => {
                                          const visita = visitasGradeCheckin.find(v => v.id === slot.visitaId);
                                          if (visita) abrirDetalhesVisita(visita);
                                        }}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium max-w-[110px] truncate ${corVisita} hover:opacity-80 cursor-pointer transition-opacity`}
                                        title={`${slot.nomeVisitante} (${slot.visitaStatus}) - Clique para ver detalhes`}
                                      >
                                        {(slot.fotoVisitante || slot.fotoSolicitante) ? (
                                          <img src={slot.fotoVisitante || slot.fotoSolicitante} alt={slot.nomeVisitante} className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-white" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
                                        )}
                                        {labelVisita}
                                      </button>
                                    ) : (
                                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                                        slot.fechado
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        <span className={`w-2 h-2 rounded-full ${slot.fechado ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                        {slot.fechado ? 'Fechado' : 'Livre'}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: cards por dia */}
                  <div className="md:hidden space-y-3">
                    {diasSemanaCheckin.map(data => {
                      const d = new Date(data + 'T12:00:00');
                      const isHoje = d.toDateString() === new Date().toDateString();
                      const fechamentoCompleto = fechamentosCheckin.find(f => f.data === data && f.ativo && !f.horario_inicio && !f.horario_fim);
                      const diaLabel = ['Seg','Ter','Qua','Qui','Sex'][diasSemanaCheckin.indexOf(data)];
                      return (
                        <div key={data} className="rounded-lg border border-gray-200 bg-white p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-semibold ${isHoje ? 'text-yellow-600' : 'text-gray-800'}`}>
                              {diaLabel}, {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                            </span>
                            {fechamentoCompleto && <span className="text-xs text-red-600 font-medium">Fechado</span>}
                          </div>
                          {fechamentoCompleto ? (
                            <div className="text-center py-4 bg-red-50 rounded-lg">
                              <p className="text-xs text-red-600">Dia fechado</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {horariosCheckin.map(horario => {
                                const slot = gradeCheckin[data]?.[horario];
                                if (!slot) return null;
                                const temVisita = !!slot.nomeVisitante;
                                const isPendente = slot.visitaStatus === 'PENDENTE';
                                const isReagendado = slot.visitaStatus === 'REAGENDADO';
                                const corVisita = isPendente
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : isReagendado
                                  ? 'bg-orange-100 text-orange-800'
                                  : slot.visitaStatus === 'APROVADO' || slot.visitaStatus === 'CHEGOU' || slot.visitaStatus === 'EM_ATENDIMENTO'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-700';
                                const labelVisita = isPendente
                                  ? 'Em análise'
                                  : isReagendado
                                  ? 'Proposta de Reagendamento'
                                  : slot.nomeVisitante;
                                return (
                                  <div key={horario} className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-100">
                                    <span className="text-xs text-gray-500 font-medium">{horario}</span>
                                    {temVisita ? (
                                      <button
                                        onClick={() => {
                                          const visita = visitasGradeCheckin.find(v => v.id === slot.visitaId);
                                          if (visita) abrirDetalhesVisita(visita);
                                        }}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium truncate w-full justify-center ${corVisita} hover:opacity-80 cursor-pointer transition-opacity`}
                                        title={`${slot.nomeVisitante} (${slot.visitaStatus}) - Clique para ver detalhes`}
                                      >
                                        {(slot.fotoVisitante || slot.fotoSolicitante) ? (
                                          <img src={slot.fotoVisitante || slot.fotoSolicitante} alt={slot.nomeVisitante} className="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-white" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
                                        )}
                                        {labelVisita}
                                      </button>
                                    ) : (
                                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                                        slot.fechado
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        <span className={`w-2 h-2 rounded-full ${slot.fechado ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                        {slot.fechado ? 'Fechado' : 'Livre'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Agenda do dia (checkin existente) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Check-in - Calendário de Slots</h2>
                <input
                  type="date"
                  className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dataSelecionada}
                  onChange={(e) => setDataSelecionada(e.target.value)}
                />
              </div>

              {slotsCheckin.length === 0 ? (
                <div className="p-6 text-center text-gray-500">Nenhum horário configurado para este dia.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {slotsCheckin.map(slot => {
                    const visita = slot.visita;
                    const status = visita ? visita.status : slot.ocupado ? 'FECHADO' : 'LIVRE';
                    const statusInfo = visita
                      ? statusConfig[visita.status]
                      : status === 'FECHADO'
                      ? { label: 'Fechado', cor: 'bg-red-100 text-red-800 border-red-300' }
                      : { label: 'Livre', cor: 'bg-green-100 text-green-800 border-green-300' };
                    return (
                      <div
                        key={slot.inicio}
                        className={`p-4 rounded-lg border shadow-sm transition hover:shadow-md ${
                          visita ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 cursor-default'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">{slot.inicio} - {slot.fim}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusInfo.cor}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        {visita ? (
                          <div className="space-y-1">
                            <div className="flex items-start gap-3">
                              {(visita.foto_visitante_url || visita.solicitante?.foto_perfil) ? (
                                <img
                                  src={visita.foto_visitante_url || visita.solicitante?.foto_perfil}
                                  alt={visita.nome_visitante}
                                  className="w-10 h-12 object-cover rounded-lg border border-gray-300 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-12 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{visita.assunto}</p>
                                <p className="text-sm text-gray-600 truncate">{visita.nome_visitante} - {visita.empresa_representada}</p>
                                <p className="text-sm text-gray-500">{visita.codigo}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 pt-2">
                              {visita.status === 'APROVADO' && (
                                <button
                                  onClick={() => {
                                    setDadosCheckin({
                                      documento_confirmado: visita.documento || '',
                                      empresa_confirmada: visita.empresa_representada || '',
                                      nome_recepcionista: user?.nome || '',
                                      observacoes: ''
                                    });
                                    setModalCheckin(visita);
                                  }}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                                >
                                  Checkin
                                </button>
                              )}
                              {visita.status === 'CHEGOU' && (
                                <button
                                  onClick={() => handleIniciarAtendimento(visita)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                                >
                                  Iniciar Atendimento
                                </button>
                              )}
                              {visita.status === 'EM_ATENDIMENTO' && (
                                <button
                                  onClick={() => abrirModalFinalizar(visita)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700"
                                >
                                  Finalizar
                                </button>
                              )}
                              {(visita.status === 'APROVADO' || visita.status === 'CHEGOU' || visita.status === 'EM_ATENDIMENTO') && (
                                <button
                                  onClick={() => abrirModalTransferir(visita)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                                >
                                  Transferir
                                </button>
                              )}
                              {visita.status === 'APROVADO' && (
                                <button
                                  onClick={() => setModalNaoCompareceu(visita)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                >
                                  Não Compareceu
                                </button>
                              )}
                              {visita.status !== 'CANCELADO' && visita.status !== 'FINALIZADO' && visita.status !== 'NAO_COMPARECEU' && (
                                <button
                                  onClick={() => abrirModalCancelar(visita)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                                >
                                  Cancelar
                                </button>
                              )}
                              <button
                                onClick={() => abrirDetalhesVisita(visita)}
                                className="inline-flex px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
                              >
                                Detalhes
                              </button>
                            </div>
                            {['CANCELADO', 'NAO_COMPARECEU'].includes(visita.status) && !slot.ocupado && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <span className="inline-block px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded mb-1">Horário Disponível</span>
                                <button
                                  onClick={() => setModalTentativaVisita(true)}
                                  className="inline-flex px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 ml-2"
                                >
                                  + Tentativa de Visita
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-500">{slot.ocupado ? 'Horário fechado' : 'Nenhuma visita agendada'}</p>
                            {!slot.ocupado && (
                              <button
                                onClick={() => setModalTentativaVisita(true)}
                                className="inline-flex px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                              >
                                + Tentativa de Visita
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {abaAtiva === 'solicitacoes' && (
          <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <label htmlFor="buscaSolicitante" className="block text-sm font-medium text-gray-700 mb-1">
                          Buscar por solicitante
                        </label>
                        <input
                          id="buscaSolicitante"
                          type="text"
                          value={buscaSolicitante}
                          onChange={(e) => setBuscaSolicitante(e.target.value)}
                          placeholder="Digite o nome do solicitante..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="relative sm:w-64">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por status</label>
                        <button
                          type="button"
                          onClick={() => setDropdownStatusAberto(!dropdownStatusAberto)}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <span className="text-sm text-gray-700 truncate">
                            {statusFiltro.length === 0 ? 'Pendentes (padrão)' : statusFiltro.length === 1 ? statusConfig[statusFiltro[0]].label : `${statusFiltro.length} selecionados`}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${dropdownStatusAberto ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {dropdownStatusAberto && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setDropdownStatusAberto(false)} />
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                              <button
                                type="button"
                                onClick={() => { setStatusFiltro([]); }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 border-b border-gray-100"
                              >
                                Pendentes (padrão)
                              </button>
                              {(Object.keys(statusConfig) as StatusVisita[]).map(status => {
                                const selecionado = statusFiltro.includes(status);
                                return (
                                  <button
                                    key={status}
                                    type="button"
                                    onClick={() => toggleStatusFiltro(status)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <span className={`w-3 h-3 rounded-sm border ${selecionado ? statusConfig[status].cor : 'bg-transparent border-gray-400'}`} />
                                    <span className={selecionado ? 'font-medium' : 'text-gray-600'}>{statusConfig[status].label}</span>
                                  </button>
                                );
                              })}
                              {statusFiltro.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => { setStatusFiltro([]); setDropdownStatusAberto(false); }}
                                  className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-100 bg-gray-50"
                                >
                                  Limpar filtros
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">Solicitações Pendentes</h2>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {visitasPendentesFiltradas.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">Nenhuma solicitação pendente.</div>
                ) : (
                  visitasPendentesFiltradas.map(visita => (
                    <div key={visita.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 flex items-start gap-3">
                          {visita.foto_visitante_url ? (
                            <img
                              src={visita.foto_visitante_url}
                              alt={visita.nome_visitante}
                              className="w-12 h-16 object-cover rounded-lg border border-gray-300 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-16 rounded-lg border border-gray-300 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{visita.assunto}</p>
                            <p className="text-sm text-gray-600">
                              <button
                                type="button"
                                onClick={() => abrirDetalhesVisita(visita)}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {visita.nome_visitante}
                              </button>
                              {' '}- {visita.empresa_representada}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(visita.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {visita.horario_inicio} - {visita.horario_fim}
                            </p>
                            {renderFluxograma(visita.status)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {podeAprovar && visita.status === 'PENDENTE' && (
                            <button
                              onClick={() => handleAprovar(visita)}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                            >
                              Aprovar
                            </button>
                          )}
                          {podeReprovar && visita.status === 'PENDENTE' && (
                            <button
                              onClick={() => setModalReprovar(visita)}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                              Reprovar
                            </button>
                          )}
                          {podeReagendar && visita.status === 'PENDENTE' && (
                            <button
                              onClick={() => {
                                setModalReagendar(visita);
                                setDataReagendamento(visita.data_visita);
                                setHoraReagendamento('');
                                setSlotsReagendamento([]);
                              }}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                            >
                              Reagendar
                            </button>
                          )}
                          {visita.status === 'REAGENDADO' && (() => {
                            let proposta: any = null;
                            try { proposta = JSON.parse(visita.observacoes || '{}')?.reagendamento_proposto; } catch {}
                            return proposta ? (
                              <>
                                <div className="text-xs text-orange-700 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
                                  <p className="font-medium">Solicitação de Reagendamento:</p>
                                  <p>Nova data: {new Date(proposta.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')}</p>
                                  <p>Horário: {proposta.horario_inicio} - {proposta.horario_fim}</p>
                                </div>
                                {podeAprovar && (
                                  <button
                                    onClick={() => handleAprovar(visita)}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                                  >
                                    Aprovar Reagendamento
                                  </button>
                                )}
                                {podeReprovar && (
                                  <button
                                    onClick={() => setModalReprovar(visita)}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                                  >
                                    Reprovar Reagendamento
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-orange-600 font-medium">Aguardando aprovação do solicitante</span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {statusFiltro.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Visitas Aprovadas/Em Andamento</h2>
              </div>
              <div className="divide-y divide-gray-100">
                      {visitasAprovadasFiltradas.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">Nenhuma visita aprovada.</div>
                      ) : (
                        visitasAprovadasFiltradas.map(visita => (
                    <div key={visita.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 flex items-start gap-3">
                          {visita.foto_visitante_url ? (
                            <img
                              src={visita.foto_visitante_url}
                              alt={visita.nome_visitante}
                              className="w-12 h-16 object-cover rounded-lg border border-gray-300 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-16 rounded-lg border border-gray-300 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusConfig[visita.status].cor}`}>
                                {statusConfig[visita.status].label}
                              </span>
                              <span className="text-sm text-gray-500">{visita.codigo}</span>
                            </div>
                            <p className="font-medium text-gray-900">{visita.assunto}</p>
                            <p className="text-sm text-gray-600">
                              <button
                                type="button"
                                onClick={() => abrirDetalhesVisita(visita)}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {visita.nome_visitante}
                              </button>
                              {' '}- {visita.empresa_representada}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(visita.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {visita.horario_inicio} - {visita.horario_fim}
                            </p>
                            {renderFluxograma(visita.status)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {visita.tempo_espera_minutos !== undefined && (
                            <p>Espera: {visita.tempo_espera_minutos} min</p>
                          )}
                          {visita.tempo_atendimento_minutos !== undefined && (
                            <p>Atendimento: {visita.tempo_atendimento_minutos} min</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            )}

            {statusFiltro.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Visitas Canceladas / Não Compareceu</h2>
              </div>
              <div className="divide-y divide-gray-100">
                      {visitasCanceladasFiltradas.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">Nenhuma visita cancelada.</div>
                      ) : (
                        visitasCanceladasFiltradas.map(visita => (
                    <div key={visita.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusConfig[visita.status].cor}`}>
                              {statusConfig[visita.status].label}
                            </span>
                            <span className="text-sm text-gray-500">{visita.codigo}</span>
                          </div>
                          <p className="font-medium text-gray-900">{visita.assunto}</p>
                          <p className="text-sm text-gray-600">
                            <button
                              type="button"
                              onClick={() => abrirDetalhesVisita(visita)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {visita.nome_visitante}
                            </button>
                            {' '}- {visita.empresa_representada}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(visita.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {visita.horario_inicio} - {visita.horario_fim}
                          </p>
                          {visita.motivo_cancelamento && (
                            <p className="text-sm text-red-600 mt-1">
                              <strong>Motivo do cancelamento:</strong> {visita.motivo_cancelamento}
                            </p>
                          )}
                          {renderFluxograma(visita.status)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            )}

            {statusFiltro.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Visitas Reprovadas</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {visitasReprovadasFiltradas.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">Nenhuma visita reprovada.</div>
                ) : (
                  visitasReprovadasFiltradas.map(visita => (
                    <div key={visita.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusConfig[visita.status].cor}`}>
                              {statusConfig[visita.status].label}
                            </span>
                            <span className="text-sm text-gray-500">{visita.codigo}</span>
                          </div>
                          <p className="font-medium text-gray-900">{visita.assunto}</p>
                          <p className="text-sm text-gray-600">
                            <button
                              type="button"
                              onClick={() => abrirDetalhesVisita(visita)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {visita.nome_visitante}
                            </button>
                            {' '}- {visita.empresa_representada}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(visita.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {visita.horario_inicio} - {visita.horario_fim}
                          </p>
                          {visita.motivo_reprovacao && (
                            <p className="text-sm text-red-600 mt-1">
                              <strong>Motivo da reprovação:</strong> {visita.motivo_reprovacao}
                            </p>
                          )}
                          {renderFluxograma(visita.status)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            )}
          </div>
        )}

        {abaAtiva === 'indicadores' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Início</label>
                  <input
                    type="date"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dataInicioIndicadores}
                    onChange={(e) => setDataInicioIndicadores(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fim</label>
                  <input
                    type="date"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dataFimIndicadores}
                    onChange={(e) => setDataFimIndicadores(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                  <input
                    type="text"
                    placeholder="Buscar fornecedor..."
                    className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={fornecedorIndicadores}
                    onChange={(e) => setFornecedorIndicadores(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && carregarIndicadores()}
                  />
                </div>
                <button
                  onClick={carregarIndicadores}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Atualizar
                </button>
              </div>
            </div>

            {indicadores && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Visitas Hoje</p>
                  <p className="text-3xl font-bold text-blue-600">{indicadores.visitas_hoje}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Total de Agendamentos</p>
                  <p className="text-3xl font-bold text-gray-700">{indicadores.total_agendamentos}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Agendamentos Realizados</p>
                  <p className="text-3xl font-bold text-indigo-600">{indicadores.agendamentos_realizados}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Agendamentos Aprovados</p>
                  <p className="text-3xl font-bold text-green-600">{indicadores.agendamentos_aprovados}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Taxa de Aprovação</p>
                  <p className="text-3xl font-bold text-green-600">{indicadores.taxa_aprovacao}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Taxa de Recusa</p>
                  <p className="text-3xl font-bold text-red-600">{indicadores.taxa_recusa}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Taxa de Cancelamento</p>
                  <p className="text-3xl font-bold text-orange-600">{indicadores.taxa_cancelamento}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Taxa de Reagendamento</p>
                  <p className="text-3xl font-bold text-yellow-600">{indicadores.taxa_reagendamento}<span className="text-sm font-normal">%</span></p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Tempo Médio para Aprovação</p>
                  <p className="text-3xl font-bold text-blue-600">{indicadores.tempo_medio_aprovacao_minutos} <span className="text-sm font-normal">min</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Tempo Médio de Espera</p>
                  <p className="text-3xl font-bold text-yellow-600">{indicadores.tempo_medio_espera_minutos} <span className="text-sm font-normal">min</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Tempo Médio de Atendimento</p>
                  <p className="text-3xl font-bold text-green-600">{indicadores.tempo_medio_atendimento_minutos} <span className="text-sm font-normal">min</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Tempo Médio de Permanência</p>
                  <p className="text-3xl font-bold text-purple-600">{indicadores.tempo_medio_permanencia_minutos} <span className="text-sm font-normal">min</span></p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Liberação pela Recepção</p>
                  <p className="text-3xl font-bold text-teal-600">{indicadores.tempo_medio_liberacao_recepcao_minutos} <span className="text-sm font-normal">min</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Solicitação → Visita</p>
                  <p className="text-3xl font-bold text-cyan-600">{indicadores.tempo_medio_solicitacao_visita_dias} <span className="text-sm font-normal">dias</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Pontualidade dos Visitantes</p>
                  <p className="text-3xl font-bold text-green-600">{indicadores.pontualidade_visitantes}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Comparecimento</p>
                  <p className="text-3xl font-bold text-emerald-600">{indicadores.comparecimento}<span className="text-sm font-normal">%</span></p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Faltas (No Show)</p>
                  <p className="text-3xl font-bold text-red-600">{indicadores.faltas_no_show}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Atendimento Concluído</p>
                  <p className="text-3xl font-bold text-green-600">{indicadores.indice_atendimento_concluido}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Taxa de Ocupação da Agenda</p>
                  <p className="text-3xl font-bold text-blue-600">{indicadores.taxa_ocupacao_agenda}<span className="text-sm font-normal">%</span></p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Total Finalizadas</p>
                  <p className="text-3xl font-bold text-emerald-600">{indicadores.total_finalizadas}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Agendamentos sem Visita Presencial</p>
                  <p className="text-3xl font-bold text-orange-600">{indicadores.agendamentos_sem_visita_presencial ?? 0}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Comprador com Maior Volume</p>
                  {indicadores.comprador_maior_volume ? (
                    <>
                      <p className="text-lg font-bold text-gray-900 truncate">{indicadores.comprador_maior_volume.nome}</p>
                      <p className="text-sm text-gray-500">{indicadores.comprador_maior_volume.quantidade} atendimentos</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum dado</p>
                  )}
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Fornecedor Mais Atendido</p>
                  {indicadores.fornecedor_mais_atendido ? (
                    <>
                      <p className="text-lg font-bold text-gray-900 truncate">{indicadores.fornecedor_mais_atendido.nome}</p>
                      <p className="text-sm text-gray-500">{indicadores.fornecedor_mais_atendido.quantidade} visitas</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum dado</p>
                  )}
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Representante com Mais Visitas</p>
                  {indicadores.representante_mais_visitas ? (
                    <>
                      <p className="text-lg font-bold text-gray-900 truncate">{indicadores.representante_mais_visitas.nome}</p>
                      <p className="text-sm text-gray-500">{indicadores.representante_mais_visitas.quantidade} visitas</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Nenhum dado</p>
                  )}
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <p className="text-sm text-gray-600">Horários Mais Utilizados</p>
                  <div className="mt-1 space-y-1">
                    {indicadores.horarios_mais_utilizados.slice(0, 3).map((h, i) => (
                      <p key={i} className="text-sm text-gray-700"><span className="font-medium">{h.faixa}:</span> {h.quantidade}</p>
                    ))}
                    {indicadores.horarios_mais_utilizados.length === 0 && <p className="text-sm text-gray-500">Nenhum dado</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(indicadores.por_status).map(([status, quantidade]) => {
                      const percent = indicadores.por_status_percentual[status] || 0;
                      const cfg = statusConfig[status as StatusVisita];
                      const bgClass = cfg.cor.split(' ').find(c => c.startsWith('bg-')) || 'bg-gray-400';
                      return (
                        <div key={status}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className={`font-medium px-2 py-0.5 rounded ${cfg.cor}`}>{cfg.label}</span>
                            <span className="text-gray-600">{quantidade} ({percent}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className={`h-2.5 rounded-full ${bgClass}`} style={{ width: `${Math.max(percent, 5)}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Eficiência e Qualidade</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Aprovação', value: indicadores.taxa_aprovacao, color: 'bg-green-500', text: 'text-green-600' },
                      { label: 'Recusa', value: indicadores.taxa_recusa, color: 'bg-red-500', text: 'text-red-600' },
                      { label: 'Cancelamento', value: indicadores.taxa_cancelamento, color: 'bg-orange-500', text: 'text-orange-600' },
                      { label: 'Reagendamento', value: indicadores.taxa_reagendamento, color: 'bg-yellow-500', text: 'text-yellow-600' },
                      { label: 'Comparecimento', value: indicadores.comparecimento, color: 'bg-emerald-500', text: 'text-emerald-600' },
                      { label: 'Faltas (No Show)', value: indicadores.faltas_no_show, color: 'bg-red-500', text: 'text-red-600' },
                      { label: 'Atendimento Concluído', value: indicadores.indice_atendimento_concluido, color: 'bg-green-500', text: 'text-green-600' },
                      { label: 'Ocupação da Agenda', value: indicadores.taxa_ocupacao_agenda, color: 'bg-blue-500', text: 'text-blue-600' },
                    ].map((item, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">{item.label}</p>
                        <p className={`text-2xl font-bold ${item.text}`}>{item.value}<span className="text-sm font-normal text-gray-500">%</span></p>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                          <div className={`h-1.5 rounded-full ${item.color}`} style={{ width: `${Math.max(item.value, 5)}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Compradores</h3>
                  <div className="space-y-3">
                    {indicadores.top_compradores.length > 0 ? indicadores.top_compradores.map((c, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-gray-900 truncate">{i + 1}. {c.nome}</span>
                          <span className="text-gray-600">{c.quantidade}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-blue-500" style={{ width: `${Math.max((c.quantidade / (indicadores.top_compradores[0].quantidade || 1)) * 100, 5)}%` }}></div>
                        </div>
                      </div>
                    )) : <p className="text-sm text-gray-500">Nenhum dado</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Fornecedores</h3>
                  <div className="space-y-3">
                    {indicadores.top_fornecedores.length > 0 ? indicadores.top_fornecedores.map((f, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-gray-900 truncate">{i + 1}. {f.nome}</span>
                          <span className="text-gray-600">{f.quantidade}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-green-500" style={{ width: `${Math.max((f.quantidade / (indicadores.top_fornecedores[0].quantidade || 1)) * 100, 5)}%` }}></div>
                        </div>
                      </div>
                    )) : <p className="text-sm text-gray-500">Nenhum dado</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Representantes</h3>
                  <div className="space-y-3">
                    {indicadores.top_representantes.length > 0 ? indicadores.top_representantes.map((r, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-gray-900 truncate">{i + 1}. {r.nome}</span>
                          <span className="text-gray-600">{r.quantidade}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-purple-500" style={{ width: `${Math.max((r.quantidade / (indicadores.top_representantes[0].quantidade || 1)) * 100, 5)}%` }}></div>
                        </div>
                      </div>
                    )) : <p className="text-sm text-gray-500">Nenhum dado</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Horários Mais Utilizados</h3>
                  <div className="space-y-3">
                    {indicadores.horarios_mais_utilizados.length > 0 ? indicadores.horarios_mais_utilizados.map((h, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-gray-900">{i + 1}. {h.faixa}</span>
                          <span className="text-gray-600">{h.quantidade}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-yellow-500" style={{ width: `${Math.max((h.quantidade / (indicadores.horarios_mais_utilizados[0].quantidade || 1)) * 100, 5)}%` }}></div>
                        </div>
                      </div>
                    )) : <p className="text-sm text-gray-500">Nenhum dado</p>}
                  </div>
                </div>

                {indicadores.evolucao_diaria.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Agendamentos por Dia</h3>
                    <div className="flex items-end gap-2 h-48 overflow-x-auto pb-2">
                      {(() => {
                        const max = Math.max(...indicadores.evolucao_diaria.map(d => d.quantidade));
                        return indicadores.evolucao_diaria.map((dia, i) => (
                          <div key={i} className="flex flex-col items-center justify-end flex-shrink-0" style={{ width: '2.5rem' }}>
                            <span className="text-xs font-semibold text-gray-700 mb-1">{dia.quantidade}</span>
                            <div className="w-6 bg-blue-500 rounded-t hover:bg-blue-600 transition-all" style={{ height: `${max ? (dia.quantidade / max) * 100 : 0}%` }}></div>
                            <span className="text-[10px] text-gray-500 mt-1 whitespace-nowrap">{dia.dia_semana}</span>
                            <span className="text-[10px] text-gray-400">{new Date(dia.data + 'T12:00:00').getDate()}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {indicadores.piores_fornecedores.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:col-span-2 lg:col-span-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Fornecedores com Piores Índices</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-600 border-b border-gray-200">
                            <th className="pb-2 font-medium">Fornecedor</th>
                            <th className="pb-2 font-medium text-center">Total</th>
                            <th className="pb-2 font-medium text-center">Faltas</th>
                            <th className="pb-2 font-medium text-center">Atrasos</th>
                            <th className="pb-2 font-medium text-center">Reprovados</th>
                            <th className="pb-2 font-medium text-center">Falta %</th>
                            <th className="pb-2 font-medium text-center">Atraso %</th>
                            <th className="pb-2 font-medium text-center">Reprovação %</th>
                            <th className="pb-2 font-medium text-center">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {indicadores.piores_fornecedores.map((f, i) => (
                            <tr key={i} className="border-b border-gray-100 last:border-0">
                              <td className="py-3 font-medium text-gray-900 truncate max-w-[12rem]">{i + 1}. {f.nome}</td>
                              <td className="py-3 text-center">{f.total_visitas}</td>
                              <td className="py-3 text-center text-red-600 font-medium">{f.faltas}</td>
                              <td className="py-3 text-center text-orange-600 font-medium">{f.atrasos}</td>
                              <td className="py-3 text-center text-yellow-600 font-medium">{f.reprovados}</td>
                              <td className="py-3 text-center">{f.taxa_falta}%</td>
                              <td className="py-3 text-center">{f.taxa_atraso}%</td>
                              <td className="py-3 text-center">{f.taxa_reprovacao}%</td>
                              <td className="py-3 text-center font-bold text-red-600">{f.score.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                      Faltas = visitas canceladas; Atrasos = check-in após 15 min do horário; Score = (2 × taxa de falta) + taxa de atraso + taxa de reprovação.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Configurar Horários */}
      {modalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-2">Configurar Horários de Atendimento</h3>
              <button onClick={() => setModalConfig(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {configDias.map((config, index) => (
                <div key={index} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 items-end p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Dia</label>
                    <select
                      className="w-full rounded-md border-gray-300 text-sm"
                      value={config.dia_semana}
                      onChange={(e) => handleAtualizarDiaConfig(index, 'dia_semana', parseInt(e.target.value))}
                    >
                      {diasSemanaCompleto.map((dia, i) => (
                        <option key={i} value={i}>{dia}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Início</label>
                    <input
                      type="time"
                      className="w-full rounded-md border-gray-300 text-sm"
                      value={config.horario_inicio}
                      onChange={(e) => handleAtualizarDiaConfig(index, 'horario_inicio', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fim</label>
                    <input
                      type="time"
                      className="w-full rounded-md border-gray-300 text-sm"
                      value={config.horario_fim}
                      onChange={(e) => handleAtualizarDiaConfig(index, 'horario_fim', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Início Almoço</label>
                    <input
                      type="time"
                      className="w-full rounded-md border-gray-300 text-sm"
                      value={config.horario_inicio_almoco || ''}
                      onChange={(e) => handleAtualizarDiaConfig(index, 'horario_inicio_almoco', e.target.value || undefined)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fim Almoço</label>
                    <input
                      type="time"
                      className="w-full rounded-md border-gray-300 text-sm"
                      value={config.horario_fim_almoco || ''}
                      onChange={(e) => handleAtualizarDiaConfig(index, 'horario_fim_almoco', e.target.value || undefined)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Intervalo (min)</label>
                      <input
                        type="number"
                        className="w-full rounded-md border-gray-300 text-sm"
                        value={config.intervalo_minutos}
                        onChange={(e) => handleAtualizarDiaConfig(index, 'intervalo_minutos', parseInt(e.target.value))}
                      />
                    </div>
                    <button
                      onClick={() => handleRemoverDiaConfig(index)}
                      className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={handleAdicionarDiaConfig}
                className="w-full py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                + Adicionar Dia
              </button>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
                <button
                  onClick={() => setModalConfig(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarConfiguracao}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Salvar Configuração
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fechar Dia */}
      {modalFechamento && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-md">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Fechar Dia</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Data: <strong>{new Date(dataSelecionada + 'T12:00:00').toLocaleDateString('pt-BR')}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <input
                  type="text"
                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={motivoFechamento}
                  onChange={(e) => setMotivoFechamento(e.target.value)}
                  placeholder="Ex: Férias, compromisso externo"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => setModalFechamento(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleFecharDia}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Fechar Dia
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reprovar */}
      {modalReprovar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-md">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Reprovar Visita</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
                <div className="space-y-2">
                  {motivosReprovacao.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="motivo_reprovacao"
                        checked={motivoReprovacao === m.motivo}
                        onChange={() => setMotivoReprovacao(m.motivo)} />
                      <span className="text-sm">{m.motivo}</span>
                      {m.gravidade && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${m.gravidade === 'baixa' ? 'bg-green-100 text-green-700' : m.gravidade === 'alta' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {m.gravidade === 'baixa' ? 'Baixa' : m.gravidade === 'alta' ? 'Alta' : 'Média'}
                        </span>
                      )}
                    </label>
                  ))}
                  {motivosReprovacao.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => { setModalReprovar(null); setMotivoReprovacao(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReprovar}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Reprovar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reagendar */}
      {modalReagendar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-md">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Reagendar Visita</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data</label>
                <input
                  type="date"
                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dataReagendamento}
                  onChange={(e) => setDataReagendamento(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Novo Horário</label>
                {carregandoSlotsReagendamento ? (
                  <div className="text-sm text-gray-500">Carregando horários...</div>
                ) : slotsReagendamento.length === 0 ? (
                  <div className="text-sm text-gray-500">Selecione uma data para ver os horários disponíveis.</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1">
                    {slotsReagendamento.map(slot => {
                      const label = `${slot.inicio} - ${slot.fim}`;
                      const selecionado = horaReagendamento === label;
                      return (
                        <button
                          key={label}
                          type="button"
                          disabled={slot.ocupado}
                          onClick={() => setHoraReagendamento(label)}
                          className={`text-xs font-medium px-2 py-2 rounded-lg border transition-colors ${
                            slot.ocupado
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : selecionado
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-orange-50'
                          }`}
                          title={slot.ocupado ? 'Horário ocupado' : 'Selecionar horário'}
                        >
                          {slot.ocupado ? 'Ocupado' : label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo do Reagendamento *</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {motivosReagendamento.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="motivo_reagendamento"
                        checked={motivoReagendamentoSelecionado === m.motivo}
                        onChange={() => setMotivoReagendamentoSelecionado(m.motivo)} />
                      <span className="text-sm">{m.motivo}</span>
                      {m.gravidade && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${m.gravidade === 'baixa' ? 'bg-green-100 text-green-700' : m.gravidade === 'alta' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {m.gravidade === 'baixa' ? 'Baixa' : m.gravidade === 'alta' ? 'Alta' : 'Média'}
                        </span>
                      )}
                    </label>
                  ))}
                  {motivosReagendamento.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => { setModalReagendar(null); setDataReagendamento(''); setHoraReagendamento(''); setSlotsReagendamento([]); setMotivoReagendamentoSelecionado(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReagendar}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                >
                  Reagendar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Checkin */}
      {modalCheckin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Checkin - {modalCheckin.codigo}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {modalCheckin.foto_visitante_url && (
                <div className="flex justify-center">
                  <img
                    src={modalCheckin.foto_visitante_url}
                    alt="Foto do visitante"
                    className="w-32 h-40 object-cover rounded-lg border border-gray-300 shadow-sm"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Documento Confirmado</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosCheckin.documento_confirmado}
                    onChange={(e) => setDadosCheckin({ ...dadosCheckin, documento_confirmado: e.target.value })}
                    placeholder={modalCheckin.documento}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa Confirmada</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosCheckin.empresa_confirmada}
                    onChange={(e) => setDadosCheckin({ ...dadosCheckin, empresa_confirmada: e.target.value })}
                    placeholder={modalCheckin.empresa_representada}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recepcionista</label>
                <input
                  type="text"
                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosCheckin.nome_recepcionista}
                  onChange={(e) => setDadosCheckin({ ...dadosCheckin, nome_recepcionista: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  rows={3}
                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosCheckin.observacoes}
                  onChange={(e) => setDadosCheckin({ ...dadosCheckin, observacoes: e.target.value })}
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => setModalCheckin(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCheckin}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Confirmar Chegada
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalhes da Visita */}
      {modalDetalhes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Detalhes da Visita - {modalDetalhes.codigo}</h3>
              <button
                onClick={() => setModalDetalhes(null)}
                className="text-gray-400 hover:text-gray-600 p-1 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {modalDetalhes.foto_visitante_url && (
                  <div className="flex-shrink-0 flex justify-center">
                    <img
                      src={modalDetalhes.foto_visitante_url}
                      alt="Foto do visitante"
                      className="w-32 h-40 object-cover rounded-lg border border-gray-300 shadow-sm"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusConfig[modalDetalhes.status].cor}`}>
                      {statusConfig[modalDetalhes.status].label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700"><strong>Assunto:</strong> {modalDetalhes.assunto}</p>
                  <p className="text-sm text-gray-700"><strong>Visitante:</strong> {modalDetalhes.nome_visitante || '-'}</p>
                  <p className="text-sm text-gray-700"><strong>Empresa:</strong> {modalDetalhes.empresa_representada}</p>
                  <p className="text-sm text-gray-700"><strong>Documento:</strong> {modalDetalhes.documento || '-'}</p>
                  {modalDetalhes.email_visitante && (
                    <p className="text-sm text-gray-700"><strong>Email:</strong> {modalDetalhes.email_visitante}</p>
                  )}
                  {modalDetalhes.telefone_visitante && (
                    <p className="text-sm text-gray-700"><strong>Telefone:</strong> {modalDetalhes.telefone_visitante}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>Data/Horário:</strong> {new Date(modalDetalhes.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {modalDetalhes.horario_inicio} - {modalDetalhes.horario_fim}
                </p>
                {modalDetalhes.comprador?.nome && (
                  <p className="text-sm text-gray-700"><strong>Comprador:</strong> {modalDetalhes.comprador.nome}</p>
                )}
                {modalDetalhes.observacoes && (
                  <p className="text-sm text-gray-700"><strong>Observações:</strong> {modalDetalhes.observacoes}</p>
                )}
                {modalDetalhes.data_chegada && (
                  <p className="text-sm text-gray-700"><strong>Chegada:</strong> {new Date(modalDetalhes.data_chegada).toLocaleString('pt-BR')}</p>
                )}
                {modalDetalhes.data_inicio_atendimento && (
                  <p className="text-sm text-gray-700"><strong>Início do Atendimento:</strong> {new Date(modalDetalhes.data_inicio_atendimento).toLocaleString('pt-BR')}</p>
                )}
                {modalDetalhes.data_fim_atendimento && (
                  <p className="text-sm text-gray-700"><strong>Fim do Atendimento:</strong> {new Date(modalDetalhes.data_fim_atendimento).toLocaleString('pt-BR')}</p>
                )}
                {modalDetalhes.tempo_espera_minutos !== undefined && (
                  <p className="text-sm text-gray-700"><strong>Tempo de Espera:</strong> {modalDetalhes.tempo_espera_minutos} min</p>
                )}
                {modalDetalhes.tempo_atendimento_minutos !== undefined && (
                  <p className="text-sm text-gray-700"><strong>Tempo de Atendimento:</strong> {modalDetalhes.tempo_atendimento_minutos} min</p>
                )}
                {modalDetalhes.motivo_cancelamento && (
                  <p className="text-sm text-red-600"><strong>Motivo do cancelamento:</strong> {modalDetalhes.motivo_cancelamento}</p>
                )}
                {modalDetalhes.motivo_reprovacao && (
                  <p className="text-sm text-red-600"><strong>Motivo da reprovação:</strong> {modalDetalhes.motivo_reprovacao}</p>
                )}
              </div>

              {renderFluxograma(modalDetalhes.status)}

              {/* Histórico de alterações */}
              {historicoVisita.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Histórico de Alterações</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {historicoVisita.map((h, idx) => (
                      <div key={h.id || idx} className="flex items-start gap-2 text-xs">
                        <div className="flex-shrink-0 mt-0.5">
                          <span className={`inline-block w-2 h-2 rounded-full ${
                            h.status_novo === 'APROVADO' ? 'bg-green-500' :
                            h.status_novo === 'REPROVADO' || h.status_novo === 'CANCELADO' ? 'bg-red-500' :
                            h.status_novo === 'REAGENDADO' ? 'bg-blue-500' :
                            h.status_novo === 'CHEGOU' ? 'bg-yellow-500' :
                            h.status_novo === 'EM_ATENDIMENTO' ? 'bg-purple-500' :
                            h.status_novo === 'FINALIZADO' ? 'bg-gray-500' :
                            'bg-gray-300'
                          }`}></span>
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-700">
                            <strong>{h.status_novo}</strong>
                            {h.status_anterior && ` (de ${h.status_anterior})`}
                            {h.observacoes && ` - ${h.observacoes}`}
                          </p>
                          <p className="text-gray-400">
                            {h.alterado_por_usuario?.nome || 'Sistema'} • {new Date(h.alterado_em).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setModalDetalhes(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL TRANSFERIR ===================== */}
      {modalTransferir && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Transferir Agendamento</h3>
              <p className="text-sm text-gray-500 mt-1">{modalTransferir.codigo} - {modalTransferir.nome_visitante}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data *</label>
                <input type="date" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTransferencia.nova_data_visita}
                  onChange={(e) => setDadosTransferencia({ ...dadosTransferencia, nova_data_visita: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Novo Horário *</label>
                <input type="time" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTransferencia.novo_horario_inicio}
                  onChange={(e) => setDadosTransferencia({ ...dadosTransferencia, novo_horario_inicio: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Transferência *</label>
                <select className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTransferencia.motivo_transferencia_texto}
                  onChange={(e) => {
                    const sel = motivosTransferencia.find(m => m.motivo === e.target.value);
                    setDadosTransferencia({ ...dadosTransferencia, motivo_transferencia_id: sel?.id || '', motivo_transferencia_texto: e.target.value });
                  }}>
                  <option value="">Selecione...</option>
                  {motivosTransferencia.map(m => <option key={m.id} value={m.motivo}>{m.motivo}{m.gravidade ? ` (${m.gravidade === 'baixa' ? 'Baixa' : m.gravidade === 'alta' ? 'Alta' : 'Média'})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <textarea rows={2} className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTransferencia.observacao_transferencia}
                  onChange={(e) => setDadosTransferencia({ ...dadosTransferencia, observacao_transferencia: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalTransferir(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleTransferir} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Transferir</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL CANCELAR COM MOTIVO ===================== */}
      {modalCancelar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Cancelar Agendamento</h3>
              <p className="text-sm text-gray-500 mt-1">{modalCancelar.codigo} - {modalCancelar.nome_visitante}</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo do Cancelamento *</label>
                <div className="space-y-2">
                  {motivosCancelamento.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="motivo_cancelamento"
                        checked={dadosCancelamento.motivo_cancelamento_texto === m.motivo}
                        onChange={() => setDadosCancelamento({ ...dadosCancelamento, motivo_cancelamento_id: m.id, motivo_cancelamento_texto: m.motivo })} />
                      <span className="text-sm">{m.motivo}</span>
                      {m.gravidade && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${m.gravidade === 'baixa' ? 'bg-green-100 text-green-700' : m.gravidade === 'alta' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {m.gravidade === 'baixa' ? 'Baixa' : m.gravidade === 'alta' ? 'Alta' : 'Média'}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <textarea rows={2} className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosCancelamento.observacao_cancelamento}
                  onChange={(e) => setDadosCancelamento({ ...dadosCancelamento, observacao_cancelamento: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalCancelar(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Voltar</button>
                <button onClick={handleCancelarComMotivo} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Confirmar Cancelamento</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL NÃO COMPARECEU ===================== */}
      {modalNaoCompareceu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Registrar Não Comparecimento</h3>
              <p className="text-sm text-gray-500 mt-1">{modalNaoCompareceu.codigo} - {modalNaoCompareceu.nome_visitante}</p>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">Confirma que o solicitante não compareceu ao horário agendado?</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <textarea rows={2} className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={obsNaoCompareceu}
                  onChange={(e) => setObsNaoCompareceu(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalNaoCompareceu(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleNaoCompareceu} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Confirmar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL FINALIZAR COM MOTIVO ===================== */}
      {modalFinalizar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Finalizar Atendimento</h3>
              <p className="text-sm text-gray-500 mt-1">{modalFinalizar.codigo} - {modalFinalizar.nome_visitante}</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resultado do Atendimento *</label>
                <div className="space-y-2">
                  {motivosFinalizacao.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="motivo_finalizacao"
                        checked={dadosFinalizacao.motivo_finalizacao_texto === m.motivo}
                        onChange={() => setDadosFinalizacao({ ...dadosFinalizacao, motivo_finalizacao_id: m.id, motivo_finalizacao_texto: m.motivo })} />
                      <span className="text-sm">{m.motivo}</span>
                      {m.gravidade && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${m.gravidade === 'baixa' ? 'bg-green-100 text-green-700' : m.gravidade === 'alta' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {m.gravidade === 'baixa' ? 'Baixa' : m.gravidade === 'alta' ? 'Alta' : 'Média'}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <textarea rows={2} className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosFinalizacao.observacao_finalizacao}
                  onChange={(e) => setDadosFinalizacao({ ...dadosFinalizacao, observacao_finalizacao: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalFinalizar(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleFinalizarComMotivo} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">Finalizar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL TENTATIVA DE VISITA ===================== */}
      {modalTentativaVisita && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Registrar Tentativa de Visita</h3>
              <p className="text-sm text-gray-500 mt-1">Fornecedor sem agendamento prévio</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Fornecedor *</label>
                <input type="text" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTentativa.fornecedor_nome}
                  onChange={(e) => setDadosTentativa({ ...dadosTentativa, fornecedor_nome: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assunto</label>
                <input type="text" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTentativa.assunto}
                  onChange={(e) => setDadosTentativa({ ...dadosTentativa, assunto: e.target.value })}
                  placeholder="Motivo da visita" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                <input type="text" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTentativa.documento}
                  onChange={(e) => setDadosTentativa({ ...dadosTentativa, documento: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <input type="text" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTentativa.empresa}
                  onChange={(e) => setDadosTentativa({ ...dadosTentativa, empresa: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosTentativa.email}
                    onChange={(e) => setDadosTentativa({ ...dadosTentativa, email: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input type="text" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosTentativa.telefone}
                    onChange={(e) => setDadosTentativa({ ...dadosTentativa, telefone: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                  <input type="date" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosTentativa.data_visita}
                    onChange={(e) => setDadosTentativa({ ...dadosTentativa, data_visita: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horário Início</label>
                  <input type="time" className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={dadosTentativa.horario_inicio}
                    onChange={(e) => setDadosTentativa({ ...dadosTentativa, horario_inicio: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea rows={2} className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dadosTentativa.observacoes}
                  onChange={(e) => setDadosTentativa({ ...dadosTentativa, observacoes: e.target.value })} />
              </div>
              <div>
                <SelfieCapture
                  onCapture={(file) => {
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setFotoTentativa(reader.result as string);
                      reader.readAsDataURL(file);
                    } else {
                      setFotoTentativa(null);
                    }
                  }}
                  fotoPreviewUrl={fotoTentativa || undefined}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalTentativaVisita(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleCriarTentativaVisita} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Registrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL DETALHES TENTATIVA DE VISITA ===================== */}
      {modalDetalhesTentativa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Detalhes da Tentativa de Visita</h3>
              <button
                onClick={() => setModalDetalhesTentativa(null)}
                className="text-gray-400 hover:text-gray-600 p-1 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {modalDetalhesTentativa.fotos && modalDetalhesTentativa.fotos.length > 0 && (
                  <div className="flex-shrink-0 flex justify-center">
                    <img
                      src={modalDetalhesTentativa.fotos[0]}
                      alt="Foto do fornecedor"
                      className="w-32 h-40 object-cover rounded-lg border border-gray-300 shadow-sm"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                      modalDetalhesTentativa.status === 'PENDENTE' ? 'bg-yellow-50 text-yellow-800 border-yellow-300' :
                      modalDetalhesTentativa.status === 'APROVADO' ? 'bg-green-50 text-green-800 border-green-300' :
                      'bg-red-50 text-red-800 border-red-300'
                    }`}>
                      {modalDetalhesTentativa.status === 'PENDENTE' ? 'Pendente' :
                       modalDetalhesTentativa.status === 'APROVADO' ? 'Aprovado' : 'Reprovado'}
                    </span>
                  </div>
                  {modalDetalhesTentativa.assunto && (
                    <p className="text-sm text-gray-700"><strong>Assunto:</strong> {modalDetalhesTentativa.assunto}</p>
                  )}
                  <p className="text-sm text-gray-700"><strong>Visitante:</strong> {modalDetalhesTentativa.fornecedor_nome || '-'}</p>
                  <p className="text-sm text-gray-700"><strong>Empresa:</strong> {modalDetalhesTentativa.empresa || '-'}</p>
                  <p className="text-sm text-gray-700"><strong>Documento:</strong> {modalDetalhesTentativa.documento || '-'}</p>
                  {modalDetalhesTentativa.email && (
                    <p className="text-sm text-gray-700"><strong>Email:</strong> {modalDetalhesTentativa.email}</p>
                  )}
                  {modalDetalhesTentativa.telefone && (
                    <p className="text-sm text-gray-700"><strong>Telefone:</strong> {modalDetalhesTentativa.telefone}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>Data:</strong> {new Date(modalDetalhesTentativa.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')}
                </p>
                {modalDetalhesTentativa.horario_inicio && (
                  <p className="text-sm text-gray-700">
                    <strong>Horário:</strong> {modalDetalhesTentativa.horario_inicio.substring(0, 5)}
                    {modalDetalhesTentativa.horario_fim ? ' - ' + modalDetalhesTentativa.horario_fim.substring(0, 5) : ''}
                  </p>
                )}
                {modalDetalhesTentativa.observacoes && (
                  <p className="text-sm text-gray-700"><strong>Observações:</strong> {modalDetalhesTentativa.observacoes}</p>
                )}
                {modalDetalhesTentativa.motivo_reprovacao && (
                  <p className="text-sm text-red-600"><strong>Motivo da reprovação:</strong> {modalDetalhesTentativa.motivo_reprovacao}</p>
                )}
                <p className="text-xs text-gray-400">
                  <strong>Registrado em:</strong> {new Date(modalDetalhesTentativa.criado_em).toLocaleString('pt-BR')}
                </p>
              </div>

              {modalDetalhesTentativa.status === 'PENDENTE' && (
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => {
                      handleAprovarTentativa(modalDetalhesTentativa.id);
                      setModalDetalhesTentativa(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    Aprovar
                  </button>
                  <button
                    onClick={() => {
                      const motivo = prompt('Motivo da reprovação:');
                      if (motivo) {
                        handleReprovarTentativa(modalDetalhesTentativa.id, motivo);
                        setModalDetalhesTentativa(null);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Reprovar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoVisitas;
